<template>
  <v-card elevation="0" class="contract-labor-form">
    <v-container fluid class="px-4 py-2">
      <!-- Indicador de Ámbito de Modificación Activo -->
      <v-alert
        v-if="isModification"
        dense
        text
        type="info"
        icon="mdi-square-edit-outline"
        class="mb-4 text-caption font-weight-medium"
      >
        Modo Edición: <strong>{{ scopeHelpText }}</strong>
      </v-alert>

      <!-- Indicador de Solo Lectura para Contrato Inactivo / Modo Visualización -->
      <v-alert
        v-if="isFormReadOnly && form.AgtID > 0"
        dense
        text
        type="warning"
        icon="mdi-lock-outline"
        class="mb-4 text-caption font-weight-medium"
      >
        Modo Solo Lectura: <strong>Contrato inactivo (no editable)</strong>
      </v-alert>

      <!-- Banner de Ampliación / Prórroga -->
      <v-card
        flat
        outlined
        class="mb-4 section-card border-warning"
        v-if="extension"
      >
        <v-card-title
          class="section-title py-2 px-4 warning lighten-5 orange--text text--darken-3 d-flex align-center"
        >
          <v-icon small class="mr-2" color="orange darken-3"
            >mdi-calendar-plus</v-icon
          >
          Ampliación / Prórroga de Contrato
        </v-card-title>
        <v-card-text class="pt-2 pb-2 caption orange--text text--darken-4">
          <v-icon x-small color="orange darken-3" class="mr-1"
            >mdi-information-outline</v-icon
          >
          Solo la <strong>Fecha Fin del Contrato</strong> está habilitada para
          extender el plazo. La nueva fecha fin debe ser posterior a la
          anterior.
        </v-card-text>
      </v-card>
      <!-- Sección: Establecimiento y SCTR -->
      <v-card
        flat
        outlined
        class="mb-4 section-card"
        :class="{ 'sctr-active-card': form.AgtIsSCTR }"
      >
        <v-card-title class="section-title py-2 px-4">
          <v-icon
            small
            class="mr-2"
            :color="form.AgtIsSCTR ? 'warning' : 'primary'"
            >mdi-domain</v-icon
          >
          Establecimiento
          <v-spacer />
          <v-chip
            v-if="form.AgtIsSCTR"
            small
            color="warning"
            text-color="white"
            class="ml-2"
          >
            <v-icon x-small left>mdi-shield-alert</v-icon>
            SCTR ACTIVO
          </v-chip>
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense align="center">
            <v-col cols="12" sm="5">
              <s-select-establishment
                ref="establishmentRef"
                v-model="form.EstID"
                :clearable="false"
                dense
                :disabled="isLaborDisabled"
                @change="onEstablishmentChanged"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <!-- SCTR: obligatorio si riesgo o por área parametrizada, opcional si no -->
              <div class="d-flex align-center">
                <v-switch
                  v-model="form.AgtIsSCTR"
                  :disabled="isSctrMandatory || isLaborDisabled"
                  color="warning"
                  hide-details
                  dense
                  class="mt-0"
                  @change="onSctrChanged"
                >
                  <template v-slot:label>
                    <span class="text-body-2 font-weight-medium">
                      SCTR
                      <v-chip
                        x-small
                        color="warning"
                        v-if="isSctrMandatory"
                        class="ml-1"
                        >Obligatorio</v-chip
                      >
                    </span>
                  </template>
                </v-switch>
              </div>
            </v-col>
            <v-col cols="12" sm="3" v-if="isSctrMandatory">
              <v-alert
                dense
                text
                type="warning"
                icon="mdi-information"
                class="mb-0 text-caption py-1"
              >
                {{
                  estIsSCTRRisk
                    ? "Establecimiento de riesgo — SCTR obligatorio"
                    : "Área parametrizada — SCTR obligatorio"
                }}
              </v-alert>
            </v-col>
          </v-row>
          <v-row dense class="mt-2" v-if="form.AgtIsSCTR">
            <v-col cols="12" sm="4">
              <s-date
                ref="sctrBeginDateRef"
                v-model="form.AgtScrtBeginDate"
                label="Fecha Inicio SCTR *"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- Sección: Origen / Requerimiento -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-briefcase-search-outline</v-icon
          >
          Origen del Contrato
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense align="center">
            <v-col cols="12" :sm="!isRequirementLocked && (!form.AgtID || Number(form.AgtID) === 0) ? 4 : 4">
              <s-select-definition
                ref="payrollRef"
                :def="1060"
                label="Planilla *"
                v-model="form.TypePayroll"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Planilla',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper',
                    label: 'ID ScireRH',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper2',
                    label: 'Código Planilla (sem,mes,etc)',
                    required: true,
                    cols: 4,
                  },
                ]"
                :disabled="isLaborDisabled || isRequirementLocked"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="!isRequirementLocked && (!form.AgtID || Number(form.AgtID) === 0)">
              <div class="d-flex align-center">
                <s-text
                  label="Jalar Requerimiento #"
                  v-model="inputReqID"
                  type="number"
                  dense
                  @keydown.enter.prevent="pullRequirementData(inputReqID)"
                />
                <v-btn
                  x-small
                  color="primary"
                  class="ml-1 mb-1"
                  height="36"
                  :loading="isSyncingRequirement"
                  :disabled="!inputReqID"
                  @click="pullRequirementData(inputReqID)"
                >
                  <v-icon small left>mdi-cloud-download-outline</v-icon>
                  Jalar
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" :sm="!isRequirementLocked && (!form.AgtID || Number(form.AgtID) === 0) ? 4 : 8" v-if="sourceLabel">
              <s-text
                label="Origen del Contrato"
                :value="sourceLabel"
                readonly
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Organización -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-sitemap-outline</v-icon
          >
          Organización
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <!-- Alerta de Tope de Vacantes Cubierto -->
          <v-alert
            v-if="vacancyQuotaInfo && vacancyQuotaInfo.isQuotaFull && !renovation && !extension && (!form.AgtID || form.AgtID == 0)"
            dense
            outlined
            type="error"
            icon="mdi-alert-octagon"
            class="mb-3"
          >
            <div class="subtitle-2 font-weight-bold">
              ⛔ Vacantes Agotadas para este Puesto
            </div>
            <div class="caption">
              El puesto seleccionado ya cuenta con <strong>{{ vacancyQuotaInfo.coveredVacancies }}</strong> contratados y cubrió el tope máximo de <strong>{{ vacancyQuotaInfo.totalVacancies }}</strong> vacantes autorizadas para esta campaña (incluyendo contrataciones con requerimiento). No se permite crear otro contrato para este puesto.
            </div>
          </v-alert>

          <!-- Banner Informativo de Vacantes Disponibles -->
          <v-alert
            v-else-if="vacancyQuotaInfo && !vacancyQuotaInfo.isQuotaFull && vacancyQuotaInfo.totalVacancies > 0 && !renovation && !extension && (!form.AgtID || form.AgtID == 0)"
            dense
            text
            type="success"
            icon="mdi-check-circle-outline"
            class="mb-3 py-1 text-caption"
          >
            Capacidad de Requerimiento: <strong>{{ vacancyQuotaInfo.coveredVacancies }}</strong> de <strong>{{ vacancyQuotaInfo.totalVacancies }}</strong> vacantes ocupadas (Disponibles: <strong>{{ vacancyQuotaInfo.availableVacancies }}</strong>).
          </v-alert>

          <!-- Aviso orientativo si seleccionó Cargo pero aún no Campaña -->
          <div
            v-else-if="form.PstID && !form.CmpID && !renovation && !extension && (!form.AgtID || form.AgtID == 0)"
            class="caption grey--text text--darken-1 mb-2 font-italic d-flex align-center"
          >
            <v-icon x-small color="grey darken-1" class="mr-1">mdi-information-outline</v-icon>
            Seleccione la campaña para verificar el cupo y disponibilidad de vacantes autorizadas para este puesto.
          </div>

          <v-alert
            v-if="isRequirementLocked"
            dense
            text
            type="info"
            icon="mdi-lock-outline"
            class="mb-3 text-caption py-1"
          >
            <div class="d-flex align-center justify-space-between flex-wrap">
              <div>
                Origen: <strong>Requerimiento #{{ form.ReqID }}</strong> — Los
                campos de estructura (Planilla, Régimen, Área, Cargo, Centro de Costo) están vinculados al requerimiento.
              </div>
              <v-btn
                x-small
                outlined
                color="primary"
                class="ml-2 font-weight-bold"
                :loading="isSyncingRequirement"
                @click="pullRequirementData(form.ReqID)"
              >
                <v-icon x-small left>mdi-sync</v-icon>
                Actualizar datos del Requerimiento
              </v-btn>
            </div>
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-select-area
                ref="areaRef"
                label="Área *"
                v-model="form.AreID"
                :text="form.AreName"
                full
                dense
                :disabled="isLaborDisabled || isRequirementLocked"
                @change="onAreaChanged"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-area
                ref="centerCostRef"
                v-model="form.CceID"
                :text="form.CceName"
                centercost
                :clearable="true"
                label="Centro de Costo"
                full
                dense
                :disabled="isLaborDisabled || isRequirementLocked"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-business-line
                ref="businessLineRef"
                v-model="form.BslID"
                :text="form.BslName"
                :cceID="form.CceID"
                :allline="1"
                :clearable="true"
                label="Línea de Negocio"
                dense
                :disabled="isLaborDisabled"
              />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" sm="4">
              <s-select-position
                ref="positionRef"
                v-model="form.PstID"
                :text="form.PstName"
                :AreID="form.AreID || 0"
                centercost
                :clearable="true"
                label="Cargo *"
                dense
                :disabled="isLaborDisabled || isRequirementLocked"
                @change="onPositionChanged"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <div class="d-flex flex-column">
                <label class="v-label theme--light text-caption mb-1"
                  >Nivel de Criticidad</label
                >
                <div class="d-flex align-center flex-wrap">
                  <v-chip
                    small
                    :color="getCriticityColor(form.PstIsBascCritic)"
                    :text-color="getCriticityTextColor(form.PstIsBascCritic)"
                    class="mt-1"
                  >
                    <v-icon x-small left>mdi-shield-alert-outline</v-icon>
                    {{
                      form.PstIsBascCritic == 0
                        ? "No Definido"
                        : form.PstIsBascCriticName || "Normal"
                    }}
                  </v-chip>

                  <!-- Botón disponible EXCLUSIVAMENTE para Personal Crítico Alto -->
                  <v-btn
                    v-if="isCriticalHigh"
                    small
                    color="primary"
                    elevation="0"
                    class="mt-1 ml-2 text-none font-weight-bold"
                    @click="showCriticalPrintModal = true"
                  >
                    <v-icon small left>mdi-printer</v-icon>
                    Formatos BASC (3)
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <s-select
                ref="cuadrilleRef"
                :label="isQuadrilleRequired ? 'Cuadrilla *' : 'Cuadrilla'"
                :items="quadrille"
                autocomplete
                item-text="GngName"
                item-value="GngID"
                v-model="form.GngID"
                :disabled="isLaborDisabled"
                dense
              />
              <div
                v-if="isQuadrilleRequired"
                class="caption warning--text mt-1"
              >
                <v-icon x-small color="warning"
                  >mdi-alert-circle-outline</v-icon
                >
                Puesto requiere cuadrilla mayor a 1
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Tipo de Contrato y Categoría -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-file-document-multiple-outline</v-icon
          >
          Tipo de Contrato y Categoría
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeContractRef"
                :def="1069"
                label="Tipo de Contrato *"
                v-model="form.TypeContract"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción tipo de contrato',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper',
                    label: 'ID ScireRH',
                    required: true,
                    cols: 4,
                  },
                ]"
                :disabled="isLaborDisabled || isRequirementLocked"
                dense
                @change="onTypeContractChanged"
                @updateData="onTypeContractItemsLoaded"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeCategoryRef"
                :def="1482"
                label="Categoría *"
                v-model="form.TypeCategory"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Categoría Sunat',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Valor E12(Col 4) SUNAT',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper2',
                    label: 'ID ScireRH',
                    required: true,
                    cols: 4,
                  },
                ]"
                :disabled="isLaborDisabled || form._isIndeterminate"
                dense
                @input="onCategoryChanged"
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="isModalidadFormativa">
              <s-select-definition
                ref="typeFormatLaborRef"
                :def="1716"
                label="Modalidad Formativa *"
                v-model="form.TypeFormatLabor"
                :disabled="isLaborDisabled"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Modalidad Formativa',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Código Sunat',
                    required: true,
                    cols: 3,
                  },
                ]"
                dense
              />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeWorkerRef"
                :def="1039"
                label="Tipo de Trabajador *"
                v-model="form.TypeWorker"
                :text="form.TypeWorkerName"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Categoría Sunat',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedAbbreviation',
                    label: 'Abreviación',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Valor T8 SUNAT',
                    required: true,
                    cols: 4,
                  },
                  {
                    key: 'DedHelper2',
                    label: 'Cod. T24 SUNAT',
                    required: true,
                    cols: 4,
                  },
                ]"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeLaborRegimeRef"
                :def="1481"
                label="Régimen Laboral *"
                v-model="form.TypeLaborRegime"
                :text="form.LaborRegimeText"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Reg. Lab.',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Código T33 Sunat',
                    required: true,
                    cols: 3,
                  },
                ]"
                :disabled="isLaborDisabled || isRequirementLocked"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeOccupationalCategoryRef"
                :def="1039"
                :dgr="4"
                label="Cat. Ocupacional *"
                v-model="form.TypeOccupationalCategory"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Grupo Funcional',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Código T24 Sunat',
                    required: true,
                    cols: 3,
                  },
                ]"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" sm="3">
              <s-select-definition
                ref="typeTurnoRef"
                :def="1278"
                label="Turno *"
                v-model="form.TypeTurno"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeSpecialSituationRef"
                :def="1070"
                label="Situación Especial"
                v-model="form.TypeSpecialSituation"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Sit. Esp.',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Cod. T35 Sunat',
                    required: true,
                    cols: 3,
                  },
                ]"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <v-col cols="12" sm="2">
              <s-text
                label="Meses de Prueba"
                v-show="!renovation"
                v-model="form.AgtMonthsProbation"
                type="number"
                :min="0"
                :max="12"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <!-- Badge de periodo de prueba para trabajadores nuevos -->
            <v-col cols="12" sm="3" v-if="isNewWorker || showProbationBadge">
              <v-alert
                dense
                text
                type="info"
                icon="mdi-information-outline"
                class="mb-0 text-caption py-1"
              >
                <span v-if="isNewWorker"
                  >Trabajador nuevo — se recomienda periodo de prueba de 3
                  meses.</span
                >
                <span v-else
                  >Cambio de puesto detectado — considerar periodo de
                  prueba.</span
                >
              </v-alert>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeSituationRef"
                :def="1045"
                label="Tipo de Costo *"
                v-model="form.TypeSituation"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Tipo Costo',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedAbbreviation',
                    label: 'Abreviacion',
                    required: true,
                    cols: 3,
                  },
                ]"
                :disabled="
                  isLaborDisabled || form._endDateLocked || isRequirementLocked
                "
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Datos Personales del Contrato -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-account-details-outline</v-icon
          >
          Datos del Trabajador en el Contrato
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeCivilStatusRef"
                :def="1071"
                label="Estado Civil *"
                v-model="form.TypeCivilStatus"
                :add-custom="$fun.isAdmin()"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Estado Civ.',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'ID Scire',
                    required: true,
                    cols: 3,
                  },
                  {
                    key: 'DedAbbreviation',
                    label: 'Abreviatura',
                    required: true,
                    cols: 3,
                  },
                ]"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <v-col cols="12" sm="2">
              <s-text
                label="N° de Hijos"
                number
                v-model="form.AgtChildrenQuantity"
                :min="0"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Remuneración y Campaña -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary">mdi-currency-usd</v-icon>
          Remuneración y Campaña
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-protected-admin-field
                ref="salaryProtectedField"
                label="Sueldo / Jornal *"
                v-model="form.AgtSalary"
                :encrypted="true"
                :agtID="Number(form.AgtID || 0)"
                :pstID="Number(form.PstID || 0)"
                :canViewByDefault="$fun.isAdmin()"
                :alwaysMasked="false"
                :disabled="isSalaryDisabled"
                dense
                @keyupEnter="$emit('keyupEnter', $event)"
                @keyup.enter="$emit('keyupEnter', $event)"
                @submit="$emit('submit', $event)"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <s-select-campaign-se
                label="Campaña *"
                v-model="form.CmpID"
                ref="CmpIDRef"
                @input="onCampaignSelected"
                @datesLoaded="onCampaignDatesLoaded"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4">
              <div class="d-flex align-center mt-2">
                <v-switch
                  v-model="form.AgtMaximunDay"
                  :disabled="isLaborDisabled"
                  color="primary"
                  hide-details
                  dense
                  class="mt-0"
                  label="Jornada Máxima"
                />
              </div>
            </v-col>
          </v-row>

          <!-- Estado de Vacantes en Campaña -->
          <v-alert
            v-if="vacancyQuotaInfo && vacancyQuotaInfo.isQuotaFull && !renovation && !extension && (!form.AgtID || form.AgtID == 0)"
            dense
            outlined
            type="error"
            icon="mdi-alert-octagon"
            class="mt-3 mb-1"
          >
            <div class="subtitle-2 font-weight-bold">
              ⛔ Vacantes Agotadas para este Puesto en la Campaña
            </div>
            <div class="caption">
              El puesto seleccionado cuenta con <strong>{{ vacancyQuotaInfo.coveredVacancies }}</strong> contratados y cubrió el tope máximo de <strong>{{ vacancyQuotaInfo.totalVacancies }}</strong> vacantes autorizadas para esta campaña. No es posible crear un nuevo contrato.
            </div>
          </v-alert>

          <v-alert
            v-else-if="vacancyQuotaInfo && !vacancyQuotaInfo.isQuotaFull && vacancyQuotaInfo.totalVacancies > 0 && !renovation && !extension && (!form.AgtID || form.AgtID == 0)"
            dense
            text
            type="success"
            icon="mdi-check-circle-outline"
            class="mt-3 mb-1 py-1 text-caption"
          >
            Capacidad de Requerimiento: <strong>{{ vacancyQuotaInfo.coveredVacancies }}</strong> de <strong>{{ vacancyQuotaInfo.totalVacancies }}</strong> vacantes ocupadas (Disponibles: <strong>{{ vacancyQuotaInfo.availableVacancies }}</strong>).
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Sección: Fechas del Contrato -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary">mdi-calendar-range</v-icon>
          Fechas del Contrato
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-date
                ref="admissionDateRef"
                v-model="form.AgtAdmissionDate"
                label="Fecha de Ingreso *"
                :disabled="isLaborDisabled || extension || renovation"
                @input="onAdmissionDateChange"
                dense
              />
            </v-col>
            <v-col>
              <s-date
                ref="beginDateRef"
                v-model="form.AgtBeginDate"
                label="Inicio del Contrato *"
                :min="
                  form.AgtAdmissionDate
                    ? form.AgtAdmissionDate.substring(0, 10)
                    : undefined
                "
                :disabled="isLaborDisabled || extension"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="!form._isIndeterminate">
              <s-date
                ref="endDateRef"
                v-model="form.AgtEndDate"
                label="Fin del Contrato *"
                :min="minEndDate"
                :disabled="isEndDateDisabled"
                :nullable="true"
                :clearable="!extension"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Régimen Pensionario y Salud (Asegurados) -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-shield-check-outline</v-icon
          >
          Régimen Pensionario y Salud
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <!-- Selector de Pensión previa -->
          <v-row
            dense
            class="mb-2"
            v-if="existingPensionsForWorker.length > 0 && !isPensionDisabled"
          >
            <v-col cols="12">
              <v-select
                label="Pensiones anteriores registradas del colaborador"
                :items="existingPensionsItems"
                v-model="selectedExistingPensionId"
                @change="onExistingPensionSelected"
                dense
                outlined
                hide-details
                prepend-icon="mdi-shield-account-outline"
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typePensionRef"
                :def="1499"
                label="Régimen Pensionario (AFP / ONP) *"
                v-model="form.TypePensionRegimen"
                :disabled="isPensionDisabled"
                :addCustom="$fun.isAdmin()"
                :customFields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Régimen',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Cod. T11 Sunat',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedFormat',
                    label: 'Max Caracteres',
                    required: false,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper2',
                    label: '% Aportes(Ref)',
                    required: false,
                    cols: 6,
                  },
                  {
                    key: 'DedAbbreviation',
                    label: 'Es AFP? (1:si , 0 o vacío:no)',
                    required: false,
                    cols: 6,
                  },
                ]"
                dense
              />
              <div v-if="isAfpPension" class="caption mt-1">
                <a
                  href="https://servicios.sbs.gob.pe/ReporteSituacionPrevisional/Afil_Consulta.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="primary--text font-weight-medium text-decoration-none d-inline-flex align-center"
                >
                  <v-icon x-small color="primary" class="mr-1"
                    >mdi-open-in-new</v-icon
                  >
                  Consulta de Afiliación SBS (CUSPP)
                </a>
              </div>
            </v-col>
            <v-col cols="12" sm="4" v-if="isAfpPension">
              <s-text
                ref="cusppRef"
                label="CUSPP (12 caracteres) *"
                v-model="form.CUSPP"
                :maxlength="12"
                :disabled="isPensionDisabled"
                dense
                @input="
                  form.CUSPP = ($event || '')
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                "
              />
            </v-col>
            <v-col cols="12" :sm="isAfpPension ? 4 : 8">
              <s-date
                ref="psnBeginDateRef"
                v-model="form.AgtPsnBeginDate"
                label="Fecha Inicio Pensión *"
                :min="minPsnBeginDate"
                :disabled="isPsnDateDisabled"
                dense
              />
            </v-col>
          </v-row>
          <!-- Selector de Salud previa -->
          <v-row
            dense
            class="mb-2 mt-2"
            v-if="existingHealthsForWorker.length > 0 && !isHealthDisabled"
          >
            <v-col cols="12">
              <v-select
                label="Regímenes de salud anteriores registrados del colaborador"
                :items="existingHealthsItems"
                v-model="selectedExistingHealthId"
                @change="onExistingHealthSelected"
                dense
                outlined
                hide-details
                prepend-icon="mdi-hospital-building"
              />
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" sm="4">
              <s-select-definition
                ref="typeHealthRef"
                :def="1509"
                label="Régimen de Salud (EsSalud / EPS) *"
                v-model="form.TypeHealthRegimen"
                :disabled="isHealthDisabled"
                :addCustom="$fun.isAdmin()"
                :customFields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Tipo Seguro',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Valor',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper2',
                    label: 'Cod. T32 SUNAT',
                    required: true,
                    cols: 4,
                  },
                ]"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="isEpsHealth">
              <s-select-definition
                ref="typeCompanyRef"
                :def="1529"
                label="Compañía EPS *"
                v-model="form.TypeCompany"
                :disabled="isHealthDisabled"
                :addCustom="$fun.isAdmin()"
                :customFields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Compañia',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Valor N° T14 SUNAT',
                    required: true,
                    cols: 6,
                  },
                  {
                    key: 'DedHelper2',
                    label: 'Valor RUC T14 SUNAT',
                    required: true,
                    cols: 4,
                  },
                ]"
                dense
              />
            </v-col>
            <v-col cols="12" :sm="isEpsHealth ? 4 : 8">
              <s-date
                ref="hthBeginDateRef"
                v-model="form.AgtHthBeginDate"
                label="Fecha Inicio Salud *"
                :min="minHthBeginDate"
                :disabled="isHthDateDisabled"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Cuentas Bancarias (Remuneración y CTS) -->
      <v-card flat outlined class="mb-4 section-card">
        <v-card-title class="section-title py-2 px-4 d-flex align-center justify-space-between flex-wrap">
          <div class="d-flex align-center">
            <v-icon small class="mr-2" color="primary">mdi-bank-outline</v-icon>
            <span>Cuentas Bancarias</span>
          </div>
          <v-tabs
            v-model="bankTab"
            dense
            right
            hide-slider
            class="shrink-tabs"
            active-class="primary--text font-weight-bold"
            style="max-width: fit-content"
          >
            <v-tab class="text-caption px-3">
              <v-icon x-small left>mdi-cash-multiple</v-icon>
              1. Haberes (Sueldo)
            </v-tab>
            <v-tab class="text-caption px-3">
              <v-icon x-small left>mdi-piggy-bank-outline</v-icon>
              2. CTS (Semestral)
            </v-tab>
          </v-tabs>
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-tabs-items v-model="bankTab">
            <!-- ── SUB-TAB 0: CUENTA DE REMUNERACIÓN (HABERES) ── -->
            <v-tab-item>
              <v-row
                dense
                class="mb-2"
                v-if="existingAccountsForSelectedBank.length > 0 && !isBankDisabled"
              >
                <v-col cols="12">
                  <v-select
                    label="Cuentas anteriores registradas para este banco (Haberes)"
                    :items="existingAccountsItems"
                    v-model="selectedExistingAccountId"
                    @change="onExistingAccountSelected"
                    dense
                    outlined
                    hide-details
                    prepend-icon="mdi-card-text-outline"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="typeEntityBankRef"
                    :def="1316"
                    label="Entidad Bancaria"
                    v-model="form.TypeEntityBank"
                    :disabled="isBankDisabled"
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Entidad Bancaria',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedAbbreviation',
                        label: 'Validar Cuenta (1=Si, 0 o vacio=No)',
                        required: false,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper',
                        label: 'Cant. Dígitos Cuenta',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper2',
                        label: 'Cod. T36 Sunat',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedFormat',
                        label: 'Cant. Dígitos CCI',
                        required: false,
                        cols: 6,
                      },
                    ]"
                    dense
                    @change="onBankChanged"
                    @updateData="onBankChanged"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="typeMoneyRef"
                    :def="1129"
                    label="Moneda"
                    v-model="form.TypeMoney"
                    :disabled="isBankDisabled"
                    dense
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Moneda',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedAbbreviation',
                        label: 'Código',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper',
                        label: 'Símbolo',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper2',
                        label: 'Abrev',
                        required: false,
                        cols: 6,
                      },
                    ]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="batStatusRef"
                    :def="1508"
                    label="Estado de Cuenta"
                    v-model="form.BatStatus"
                    :disabled="isBankDisabled"
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Situación de la cuenta',
                        required: true,
                        cols: 6,
                      },
                    ]"
                    dense
                  />
                </v-col>
              </v-row>
              <v-row dense class="mt-2" align="start">
                <v-col cols="12" sm="6">
                  <s-text
                    ref="batAccountNumberRef"
                    :label="`Número de Cuenta ${
                      bankAccountLength ? '(' + bankAccountLength + ' díg.)' : ''
                    } ${form.BatStatus == 3 ? '*' : ''}`"
                    v-model="form.BatAccountNumber"
                    :maxlength="bankAccountLength || 20"
                    :disabled="isBankDisabled"
                    dense
                    @input="
                      form.BatAccountNumber = ($event || '').replace(/\D/g, '')
                    "
                  />
                  <div
                    v-if="accountPrefixHint"
                    class="caption grey--text text--darken-1 mt-1"
                  >
                    <v-icon x-small color="info">mdi-information-outline</v-icon>
                    {{ accountPrefixHint }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-text
                    ref="batCciRef"
                    :label="`CCI ${
                      bankCciLength
                        ? '(' + bankCciLength + ' díg.)'
                        : '(20 dígitos)'
                    }`"
                    v-model="form.BatCCI"
                    :maxlength="bankCciLength || 20"
                    :disabled="isBankDisabled"
                    dense
                    @input="form.BatCCI = ($event || '').replace(/\D/g, '')"
                  />
                  <div
                    v-if="cciHint"
                    class="caption grey--text text--darken-1 mt-1"
                  >
                    <v-icon x-small color="info">mdi-information-outline</v-icon>
                    {{ cciHint }}
                  </div>
                </v-col>
              </v-row>
              <!-- Fila de generación de correlativo (solo aplica para BCP: DedValue 2) -->
              <v-row
                dense
                class="mt-1"
                v-if="isBcpBank && form.BatStatus != 3 && !locked"
              >
                <v-col cols="12" class="d-flex align-center">
                  <v-checkbox
                    v-model="NumCorrelative"
                    label="Generar N° de Cuenta Correlativo (Interno BCP)"
                    dense
                    hide-details
                    class="mt-0 pt-0"
                    :disabled="isBankDisabled"
                    @change="onCorrelativeChange"
                  />
                  <v-btn
                    v-if="NumCorrelative"
                    x-small
                    text
                    color="primary"
                    class="ml-2"
                    :disabled="isBankDisabled"
                    @click="onCorrelativeChange(true)"
                    :loading="generatingCorrelative"
                    title="Recalcular correlativo"
                  >
                    <v-icon x-small left>mdi-refresh</v-icon> Actualizar Correlativo
                  </v-btn>
                  <span class="caption grey--text ml-2">
                    Genera el número correlativo para cuentas de haberes BCP
                  </span>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- ── SUB-TAB 1: CUENTA DE CTS ── -->
            <v-tab-item>
              <v-alert
                dense
                text
                type="info"
                icon="mdi-piggy-bank-outline"
                class="mb-3 text-caption d-flex align-center"
              >
                <div class="d-flex align-center justify-space-between w-100 flex-wrap">
                  <span>
                    <strong>Cuenta CTS (Compensación por Tiempo de Servicios):</strong>
                    Cuenta bancaria intangible destinada a los abonos semestrales (Mayo y Noviembre).
                  </span>
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    class="ml-auto my-1"
                    :disabled="isBankDisabled"
                    @click="copyFromHaberesToCts"
                    title="Copiar datos de la cuenta de remuneración a CTS"
                  >
                    <v-icon x-small left>mdi-content-copy</v-icon> Copiar de Haberes
                  </v-btn>
                </div>
              </v-alert>
              <v-row
                dense
                class="mb-2"
                v-if="existingCtsAccountsForSelectedBank.length > 0 && !isBankDisabled"
              >
                <v-col cols="12">
                  <v-select
                    label="Cuentas anteriores registradas para este banco (CTS)"
                    :items="existingCtsAccountsItems"
                    v-model="selectedExistingCtsAccountId"
                    @change="onExistingCtsAccountSelected"
                    dense
                    outlined
                    hide-details
                    prepend-icon="mdi-card-text-outline"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="typeEntityBankCtsRef"
                    :def="1316"
                    label="Entidad Bancaria CTS"
                    v-model="form.TypeEntityBank_CTS"
                    :disabled="isBankDisabled"
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Entidad Bancaria',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedAbbreviation',
                        label: 'Validar Cuenta (1=Si, 0 o vacio=No)',
                        required: false,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper',
                        label: 'Cant. Dígitos Cuenta',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper2',
                        label: 'Cod. T36 Sunat',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedFormat',
                        label: 'Cant. Dígitos CCI',
                        required: false,
                        cols: 6,
                      },
                    ]"
                    dense
                    @change="onCtsBankChanged"
                    @updateData="onCtsBankChanged"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="typeMoneyCtsRef"
                    :def="1129"
                    label="Moneda CTS"
                    v-model="form.TypeMoney_CTS"
                    :disabled="isBankDisabled"
                    dense
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Moneda',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedAbbreviation',
                        label: 'Código',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper',
                        label: 'Símbolo',
                        required: true,
                        cols: 6,
                      },
                      {
                        key: 'DedHelper2',
                        label: 'Abrev',
                        required: false,
                        cols: 6,
                      },
                    ]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <s-select-definition
                    ref="batStatusCtsRef"
                    :def="1508"
                    label="Estado de Cuenta CTS"
                    v-model="form.BatStatus_CTS"
                    :disabled="isBankDisabled"
                    :addCustom="$fun.isAdmin()"
                    :customFields="[
                      {
                        key: 'DedDescription',
                        label: 'Situación de la cuenta',
                        required: true,
                        cols: 6,
                      },
                    ]"
                    dense
                  />
                </v-col>
              </v-row>
              <v-row dense class="mt-2" align="start">
                <v-col cols="12" sm="6">
                  <s-text
                    ref="batAccountNumberCtsRef"
                    :label="`Número de Cuenta CTS ${
                      bankAccountLength_CTS ? '(' + bankAccountLength_CTS + ' díg.)' : ''
                    } ${form.BatStatus_CTS == 3 ? '*' : ''}`"
                    v-model="form.BatAccountNumber_CTS"
                    :maxlength="bankAccountLength_CTS || 20"
                    :disabled="isBankDisabled"
                    dense
                    @input="
                      form.BatAccountNumber_CTS = ($event || '').replace(/\D/g, '')
                    "
                  />
                  <div
                    v-if="accountPrefixHint_CTS"
                    class="caption grey--text text--darken-1 mt-1"
                  >
                    <v-icon x-small color="info">mdi-information-outline</v-icon>
                    {{ accountPrefixHint_CTS }}
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <s-text
                    ref="batCciCtsRef"
                    :label="`CCI CTS ${
                      bankCciLength_CTS
                        ? '(' + bankCciLength_CTS + ' díg.)'
                        : '(20 dígitos)'
                    }`"
                    v-model="form.BatCCI_CTS"
                    :maxlength="bankCciLength_CTS || 20"
                    :disabled="isBankDisabled"
                    dense
                    @input="form.BatCCI_CTS = ($event || '').replace(/\D/g, '')"
                  />
                  <div
                    v-if="cciHint_CTS"
                    class="caption grey--text text--darken-1 mt-1"
                  >
                    <v-icon x-small color="info">mdi-information-outline</v-icon>
                    {{ cciHint_CTS }}
                  </div>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>

      <!-- Sección: Cese (si aplica) -->
      <v-card
        flat
        outlined
        class="mb-4 section-card"
        v-if="!renovation && !extension && form.AgtID > 0"
      >
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="error"
            >mdi-account-off-outline</v-icon
          >
          Cese
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense align="center">
            <v-col cols="12" sm="3">
              <v-switch
                v-model="registerCessation"
                label="Registrar Cese"
                color="error"
                hide-details
                dense
                class="mt-0"
                :disabled="isLaborDisabled"
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="registerCessation">
              <s-select-definition
                ref="terminationRef"
                :def="1051"
                label="Motivo de Cese"
                :clearable="true"
                v-model="form.TypeTermination"
                :add-custom="$fun.isAdmin()"
                :disabled="isLaborDisabled"
                :custom-fields="[
                  {
                    key: 'DedDescription',
                    label: 'Descripción Motivo Cese',
                    required: true,
                    cols: 9,
                  },
                  {
                    key: 'DedHelper',
                    label: 'Cod. T17 SUNAT',
                    required: true,
                    cols: 3,
                  },
                ]"
                dense
              />
            </v-col>
            <v-col cols="12" sm="4" v-if="registerCessation">
              <s-date
                v-model="form.AgtTerminationDate"
                label="Fecha de Cese"
                :disabled="isLaborDisabled"
                dense
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Sección: Descripción del Servicio (solo para contrato por servicio específico) -->
      <v-card
        flat
        outlined
        class="mb-4 section-card"
        v-if="form.TypeContract == 9"
      >
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="primary"
            >mdi-text-box-outline</v-icon
          >
          Descripción del Servicio
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <s-textarea
            label="Describa el servicio específico"
            :height="80"
            v-model="form.AgtServiceDescription"
            dense
          />
        </v-card-text>
      </v-card>

      <!-- Modal de Impresión de Formatos BASC para Personal Crítico Alto -->
      <CriticalFormatsPrintModal
        v-model="showCriticalPrintModal"
        :worker-data="datawork"
        :contract-data="form"
      />
    </v-container>
  </v-card>
</template>

<script>
import _sWorkertService from "@/services/HumanResource/WorkerService.js";
import _sQuadriService from "@/services/HumanResource/hmnGang.js";
import _sPositionService from "@/services/General/PositionService.js";
import ServiceEstablishment from "@/services/HumanResource/HmnEstablishment";
import SProtectedAdminField from "@/components/Utils/RecursosHm/sProtectedAdminField.vue";
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import _sGenParam from "@/services/General/ParameterService.js";
import _sHmnStaffingRequirement from "@/services/HumanResource/RecruitmentSelection/HmnStaffingRequirement.js";
import CriticalFormatsPrintModal from "./CriticalFormats/CriticalFormatsPrintModal.vue";

export default {
  name: "ContractLaborForm",
  components: {
    SProtectedAdminField,
    CriticalFormatsPrintModal,
  },
  props: {
    datawork: { type: Object, default: () => ({}) },
    extension: { type: Boolean, default: false },
    renovation: { type: Boolean, default: false },
    sourceLabel: { type: String, default: null },
    isNewWorker: { type: Boolean, default: false },
    lastPstID: { type: Number, default: 0 }, // ultimo puesto para detectar cambio
    SctrMandatoryAreaIds_RRHH: {
      type: [Array, String, Number],
      default: () => [24],
    },
    RequireQuadrillePstIds_RRHH: {
      type: [Array, String, Number],
      default: () => [],
    },
    isViewOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      form: {
        AgtID: 0,
        WkrID: 0,
        NtpID: 0,
        TypePayroll: 1,
        AreID: null,
        AreName: null,
        CceID: null,
        CceName: null,
        BslID: null,
        PstID: null,
        PstName: null,
        PstIsBascCritic: 0,
        PstIsBascCriticName: null,
        GngID: 1,
        TypeContract: 11,
        TypeCategory: 1,
        TypeFormatLabor: null,
        TypeWorker: null,
        TypeWorkerName: "",
        TypeLaborRegime: 3,
        TypeOccupationalCategory: 4,
        EstID: 1,
        AgtIsSCTR: false,
        AgtScrtBeginDate: null,
        TypeCivilStatus: 1,
        AgtChildrenQuantity: 0,
        TypeTurno: 1,
        TypeSituation: 2,
        TypeSpecialSituation: 1,
        AgtMonthsProbation: null,
        AgtSalary: null,
        CmpID: null,
        AgtMaximunDay: true, // default ON
        AgtAdmissionDate: null,
        AgtBeginDate: null,
        AgtEndDate: null,
        AgtTerminationDate: null,
        AgtServiceDescription: null,
        TypeTermination: null,
        _isIndeterminate: false,
        _endDateLocked: true,
        AgtRenovation: 0,
        AgtRenewedFromID: null,
        ReqID: null,

        // Pensión (HmnPension)
        TypePensionRegimen: 2,
        CUSPP: null,
        PsnAttach: null,

        // Salud (HmnHealth)
        TypeHealthRegimen: 1,
        TypeCompany: null,

        // Banco Remuneración (HmnBankAccount)
        TypeEntityBank: 2,
        TypePay: 1,
        TypeMoney: 1,
        BatStatus: 3,
        BatAccountNumber: null,
        BatCCI: null,

        // Banco CTS (HmnBankAccount - CTS)
        BatID_CTS: null,
        TypeEntityBank_CTS: 2,
        TypePay_CTS: 2,
        TypeMoney_CTS: 1,
        BatStatus_CTS: 1,
        BatAccountNumber_CTS: null,
        BatCCI_CTS: null,
      },
      bankTab: 0,
      establishmentsList: [],
      estIsSCTRRisk: false,
      registerCessation: false,
      quadrille: [],
      generatingCorrelative: false,
      NumCorrelative: false,
      bankAccountLength: null,
      accountPrefixRequired: null,
      bankDedAbbreviation: null,
      bankCciLength: null,
      bankAccountLength_CTS: null,
      accountPrefixRequired_CTS: null,
      bankDedAbbreviation_CTS: null,
      bankCciLength_CTS: null,
      financialData: { BankAccounts: [], Pensions: [], Healths: [] },
      selectedExistingAccountId: null,
      selectedExistingCtsAccountId: null,
      selectedExistingPensionId: null,
      selectedExistingHealthId: null,
      modificationScope: "MOD_LABORAL",
      isHydrating: false,
      previousCmpID: null,
      isUserChangingCampaign: false,
      fetchedSctrAreaIds: null,
      fetchedQuadrillePstIds: null,
      vacancyQuotaInfo: null,
      isCheckingVacancy: false,
      isSyncingRequirement: false,
      inputReqID: null,
      showCriticalPrintModal: false,
    };
  },

  computed: {
    isCriticalHigh() {
      const name = (this.form.PstIsBascCriticName || "").toUpperCase();
      const isHighByName =
        name.includes("ALTO") &&
        (name.includes("CRITIC") || name.includes("CRÍT"));
      const isHighByValue = Number(this.form.PstIsBascCritic) >= 4;
      return isHighByName || isHighByValue;
    },

    parsedSctrMandatoryAreaIds() {
      const rawVal =
        this.SctrMandatoryAreaIds_RRHH !== undefined &&
        this.SctrMandatoryAreaIds_RRHH !== null &&
        this.SctrMandatoryAreaIds_RRHH !== "" &&
        JSON.stringify(this.SctrMandatoryAreaIds_RRHH) !== JSON.stringify([24])
          ? this.SctrMandatoryAreaIds_RRHH
          : this.fetchedSctrAreaIds !== null
          ? this.fetchedSctrAreaIds
          : this.SctrMandatoryAreaIds_RRHH;

      return this.parseIdsToArray(rawVal, [24]);
    },

    parsedRequireQuadrillePstIds() {
      const rawVal =
        this.RequireQuadrillePstIds_RRHH !== undefined &&
        this.RequireQuadrillePstIds_RRHH !== null &&
        this.RequireQuadrillePstIds_RRHH !== "" &&
        (!Array.isArray(this.RequireQuadrillePstIds_RRHH) ||
          this.RequireQuadrillePstIds_RRHH.length > 0)
          ? this.RequireQuadrillePstIds_RRHH
          : this.fetchedQuadrillePstIds !== null
          ? this.fetchedQuadrillePstIds
          : this.RequireQuadrillePstIds_RRHH;

      return this.parseIdsToArray(rawVal, []);
    },
    isAreaSctrMandatory() {
      if (!this.form.AreID) return false;
      const areID = Number(this.form.AreID);
      return this.parsedSctrMandatoryAreaIds.includes(areID);
    },
    isSctrMandatory() {
      return this.estIsSCTRRisk || this.isAreaSctrMandatory;
    },
    isQuadrilleRequired() {
      if (!this.form.PstID) return false;
      const pstID = Number(this.form.PstID);
      return this.parsedRequireQuadrillePstIds.includes(pstID);
    },
    isRequirementLocked() {
      return (
        !this.renovation && !!(this.form.ReqID && Number(this.form.ReqID) > 0)
      );
    },
    locked() {
      return this.extension || this.renovation;
    },
    isContractInactive() {
      // console.log("el estado del contrato es ", this.form.AgtStatus);
      return !!(
        this.form.AgtID > 0 &&
        this.form.AgtStatus == 2 &&
        !this.extension &&
        !this.renovation
      );
    },
    isFormReadOnly() {
      return this.isViewOnly || this.isContractInactive;
    },
    isModification() {
      return !!(
        this.form.AgtID > 0 &&
        !this.extension &&
        !this.renovation &&
        !this.isFormReadOnly
      );
    },
    isLaborDisabled() {
      if (this.isFormReadOnly) return true;
      if (this.extension) return true;
      if (this.renovation) return false;
      if (this.isModification) {
        return !(
          this.modificationScope === "MOD_LABORAL" ||
          this.modificationScope === "MOD_ALL"
        );
      }
      return false;
    },
    isSalaryEmptyOrZero() {
      const s = this.form.AgtSalary;
      if (s === null || s === undefined || s === "") return true;
      if (typeof s === "number") return s <= 0;
      if (typeof s === "string") {
        const trimmed = s.trim();
        if (!trimmed) return true;
        if (trimmed === "*****") return false;
        const num = parseFloat(trimmed);
        if (!isNaN(num) && isFinite(num) && String(num) === trimmed) {
          return num <= 0;
        }
        try {
          const dec = this.$fun?.decryptSalary
            ? this.$fun.decryptSalary(trimmed)
            : "";
          const decNum = parseFloat(dec);
          if (!isNaN(decNum) && isFinite(decNum)) {
            return decNum <= 0;
          }
        } catch {
          return true;
        }
      }
      return false;
    },
    isSalaryDisabled() {
      if (this.isFormReadOnly) return true;
      // El lápiz del sueldo protegido siempre debe permitir abrir el modal de autenticación admin para registrar/corregir el sueldo
      return false;
    },
    isPensionDisabled() {
      if (this.isFormReadOnly) return true;
      // Si el trabajador NO cuenta con pensión previa en BD o no tiene régimen seleccionado, permitir registrarlo
      if (
        this.existingPensionsForWorker.length === 0 ||
        !this.form.TypePensionRegimen
      ) {
        return false;
      }
      if (this.extension) return true;
      if (this.isModification) {
        return !(
          this.modificationScope === "MOD_PENSION" ||
          this.modificationScope === "MOD_ALL"
        );
      }
      return false;
    },
    isHealthDisabled() {
      if (this.isFormReadOnly) return true;
      // Si el trabajador NO cuenta con régimen de salud previo en BD o no tiene régimen seleccionado, permitir registrarlo
      if (
        this.existingHealthsForWorker.length === 0 ||
        !this.form.TypeHealthRegimen
      ) {
        return false;
      }
      if (this.extension) return true;
      if (this.isModification) {
        return !(
          this.modificationScope === "MOD_SALUD" ||
          this.modificationScope === "MOD_ALL"
        );
      }
      return false;
    },
    isBankDisabled() {
      if (this.isFormReadOnly) return true;
      // Si el trabajador NO cuenta con cuenta bancaria previa en BD o no tiene número de cuenta registrado, permitir registrarlo
      if (
        this.existingBankAccountsForWorker.length === 0 ||
        !this.form.BatAccountNumber
      ) {
        return false;
      }
      if (this.extension) return true;
      if (this.isModification) {
        return !(
          this.modificationScope === "MOD_CUENTA" ||
          this.modificationScope === "MOD_ALL"
        );
      }
      return false;
    },
    isEndDateDisabled() {
      if (this.isFormReadOnly) return true;
      if (this.extension) return false; // En ampliación / prórroga permanece activo
      if (this.renovation) return false;
      if (this.isModification) {
        return !(
          this.modificationScope === "MOD_LABORAL" ||
          this.modificationScope === "MOD_ALL"
        );
      }
      return this.form._endDateLocked;
    },
    isPsnDateDisabled() {
      if (this.isPensionDisabled) return true;
      if (
        this.selectedExistingPensionId === 0 ||
        this.existingPensionsForWorker.length === 0
      ) {
        return false;
      }
      if (!this.isModification) return true;
      return false;
    },
    isHthDateDisabled() {
      if (this.isHealthDisabled) return true;
      if (
        this.selectedExistingHealthId === 0 ||
        this.existingHealthsForWorker.length === 0
      ) {
        return false;
      }
      if (!this.isModification) return true;
      return false;
    },
    isBatDateDisabled() {
      if (this.isBankDisabled) return true;
      if (
        this.selectedExistingAccountId === 0 ||
        this.existingBankAccountsForWorker.length === 0
      ) {
        return false;
      }
      if (!this.isModification) return true;
      return false;
    },
    modificationScopeOptions() {
      return [
        {
          text: "📑 Modificación de Condiciones Laborales (Puesto, Área, Salario, Horario)",
          value: "MOD_LABORAL",
        },
        {
          text: "🛡️ Cambio de Régimen Pensionario (AFP / ONP)",
          value: "MOD_PENSION",
        },
        {
          text: "🏥 Cambio de Régimen de Salud (EsSalud / EPS)",
          value: "MOD_SALUD",
        },
        {
          text: "🏦 Cambio de Cuenta Bancaria de Remuneración",
          value: "MOD_CUENTA",
        },
        {
          text: "🔓 Modificación General (Todos los campos habilitados)",
          value: "MOD_ALL",
        },
      ];
    },
    scopeHelpText() {
      if (this.modificationScope === "MOD_LABORAL")
        return "Solo datos laborales editables";
      if (this.modificationScope === "MOD_PENSION")
        return "Solo sección Pensión editable";
      if (this.modificationScope === "MOD_SALUD")
        return "Solo sección Salud editable";
      if (this.modificationScope === "MOD_CUENTA")
        return "Solo sección Cuenta Bancaria editable";
      return "Todos los campos habilitados";
    },
    minPsnBeginDate() {
      return this.form.AgtBeginDate;
    },
    minHthBeginDate() {
      return this.form.AgtBeginDate;
    },
    minBatBeginDate() {
      return this.form.AgtBeginDate;
    },
    minEndDate() {
      if (this.extension && this.form._prevAgtEndDate) {
        const prev = new Date(this.form._prevAgtEndDate + "T00:00:00");
        prev.setDate(prev.getDate() + 1);
        return prev.toISOString().substring(0, 10);
      }
      return this.form.AgtBeginDate
        ? this.form.AgtBeginDate.substring(0, 10)
        : undefined;
    },
    isModalidadFormativa() {
      return this.form.TypeCategory == 5;
    },
    isAfpPension() {
      return this.form.TypePensionRegimen && this.form.TypePensionRegimen != 2;
    },
    isEpsHealth() {
      return this.form.TypeHealthRegimen == 2;
    },
    isBcpBank() {
      return this.form.TypeEntityBank == 2;
    },
    accountPrefixHint() {
      const isFormatStrict =
        this.bankDedAbbreviation == 1 || this.bankDedAbbreviation === "1";
      if (isFormatStrict && this.accountPrefixRequired) {
        return `La cuenta debe comenzar con "${
          this.accountPrefixRequired
        }" y tener ${this.bankAccountLength || ""} dígitos`;
      }
      if (this.bankAccountLength) {
        return `Longitud esperada: ${this.bankAccountLength} dígitos`;
      }
      return "";
    },
    cciHint() {
      const parts = [];
      if (this.bankCciLength) {
        parts.push(`${this.bankCciLength} dígitos`);
      }
      if (this.accountPrefixRequired) {
        parts.push(`debe comenzar con "${this.accountPrefixRequired}"`);
      }
      if (parts.length > 0) {
        return `CCI: ${parts.join(" - ")}`;
      }
      return "";
    },
    accountPrefixHint_CTS() {
      const isFormatStrict =
        this.bankDedAbbreviation_CTS == 1 || this.bankDedAbbreviation_CTS === "1";
      if (
        isFormatStrict &&
        this.accountPrefixRequired_CTS &&
        this.form.BatStatus_CTS == 3
      ) {
        return `La cuenta CTS debe comenzar con "${this.accountPrefixRequired_CTS}" según la entidad seleccionada.`;
      }
      return "";
    },
    cciHint_CTS() {
      const parts = [];
      if (this.bankCciLength_CTS) {
        parts.push(`${this.bankCciLength_CTS} dígitos`);
      }
      if (this.accountPrefixRequired_CTS) {
        parts.push(`debe comenzar con "${this.accountPrefixRequired_CTS}"`);
      }
      if (parts.length > 0) {
        return `CCI CTS: ${parts.join(" - ")}`;
      }
      return "";
    },
    showProbationBadge() {
      // Mostrar badge si cambia de puesto (y no es nuevo)
      return (
        !this.isNewWorker &&
        this.lastPstID > 0 &&
        this.form.PstID &&
        this.form.PstID != this.lastPstID
      );
    },
    existingBankAccountsForWorker() {
      if (!this.financialData?.BankAccounts) return [];
      return this.financialData.BankAccounts.filter((a) => a.SecStatus === 1);
    },
    existingAccountsForSelectedBank() {
      if (!this.financialData?.BankAccounts) return [];
      return this.financialData.BankAccounts.filter(
        (a) =>
          a.TypeEntityBank === this.form.TypeEntityBank &&
          (a.TypePay === 1 || !a.TypePay) &&
          a.SecStatus === 1,
      );
    },
    existingAccountsItems() {
      const list = this.existingAccountsForSelectedBank.map((a) => {
        const typePayLabel = a.TypePayName;
        const typeMoneyLabel = a.TypeMoneyName;
        const entityBankLabel = a.TypeEntityBankName;
        return {
          text: `${entityBankLabel} - ${typePayLabel} - ${a.BatAccountNumber} (${typeMoneyLabel})`,
          value: a.BatID,
        };
      });
      list.push({ text: "+ Registrar nueva cuenta...", value: 0 });
      return list;
    },
    existingCtsAccountsForSelectedBank() {
      if (!this.financialData?.BankAccounts) return [];
      return this.financialData.BankAccounts.filter(
        (a) =>
          a.TypeEntityBank === this.form.TypeEntityBank_CTS &&
          (a.TypePay === 2 || a.TypeAccountPurpose === 2) &&
          a.SecStatus === 1,
      );
    },
    existingCtsAccountsItems() {
      const list = this.existingCtsAccountsForSelectedBank.map((a) => {
        const typePayLabel = a.TypePayName;
        const typeMoneyLabel = a.TypeMoneyName;
        const entityBankLabel = a.TypeEntityBankName;
        return {
          text: `${entityBankLabel} - ${typePayLabel} - ${a.BatAccountNumber} (${typeMoneyLabel})`,
          value: a.BatID,
        };
      });
      list.push({ text: "+ Registrar nueva cuenta CTS...", value: 0 });
      return list;
    },
    existingPensionsForWorker() {
      if (!this.financialData?.Pensions) return [];
      return this.financialData.Pensions.filter((p) => p.SecStatus === 1);
    },
    existingPensionsItems() {
      const list = this.existingPensionsForWorker.map((p) => {
        const cusppText = p.CUSPP ? ` - CUSPP: ${p.CUSPP}` : "";
        const defaultText = p.PsnIsDefault === 1 ? " (Predeterminado)" : "";
        const regimeName =
          p.TypePensionRegimenName ||
          (p.TypePensionRegimen === 2
            ? "ONP"
            : p.TypePensionRegimen != 2
            ? "AFP"
            : `Régimen #${p.TypePensionRegimen}`);
        return {
          text: `${regimeName}${cusppText}${defaultText}`,
          value: p.PsnID,
        };
      });
      list.push({ text: "+ Registrar nuevo régimen de pensión...", value: 0 });
      return list;
    },
    existingHealthsForWorker() {
      if (!this.financialData?.Healths) return [];
      return this.financialData.Healths.filter((h) => h.SecStatus === 1);
    },
    existingHealthsItems() {
      // console.log(
      //   "this.existingHealthsForWorker",
      //   this.existingHealthsForWorker,
      // );

      const list = this.existingHealthsForWorker.map((h) => {
        const defaultText = h.HthIsDefault === 1 ? " (Predeterminado)" : "";
        const companyText = h.TypeCompany ? ` - ${h.TypeCompanyName}` : "";
        const regimeName =
          h.TypeHealthRegimenName ||
          (h.TypeHealthRegimen === 1
            ? "EsSalud"
            : h.TypeHealthRegimen === 2
            ? "EPS"
            : `Régimen #${h.TypeHealthRegimen}`);
        return {
          text: `${regimeName}${companyText}${defaultText}`,
          value: h.HthID,
        };
      });
      list.push({ text: "+ Registrar nuevo régimen de salud...", value: 0 });
      return list;
    },
  },

  watch: {
    datawork: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val) return;
        this.hydrateForm(val);
      },
    },
    isNewWorker: {
      immediate: true,
      handler(val) {
        if (val && !this.form.AgtMonthsProbation) {
          this.form.AgtMonthsProbation = 3;
        }
      },
    },
    "form.AgtBeginDate": {
      handler(newVal) {
        if (newVal) {
          this.form.AgtPsnBeginDate = newVal;
          this.form.AgtHthBeginDate = newVal;
          this.form.AgtBatBeginDate = newVal;
          this.form.AgtScrtBeginDate = newVal;
        }
      },
    },
    "form.AreID": {
      handler(newVal) {
        this.checkAreaSctrRequirement(newVal);
      },
    },
    "form.BslID": {
      handler(newVal) {
        if (!newVal) {
          this.form.BslName = null;
        }
      },
    },
    "form.EstID": {
      handler(val) {
        this.checkEstablishmentRisk(val);
      },
    },
    "form.CmpID": {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.checkVacancyQuota();
          });
        }
      },
    },
    "form.PstID": {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.checkVacancyQuota();
          });
        }
      },
    },
    "form.ReqID": {
      handler() {
        this.$nextTick(() => {
          this.checkVacancyQuota();
        });
      },
    },
    registerCessation(val) {
      if (val && !this.form.AgtTerminationDate) {
        const todayStr = new Date().toISOString().substring(0, 10);
        this.form.AgtTerminationDate = this.form.AgtEndDate
          ? String(this.form.AgtEndDate).substring(0, 10)
          : todayStr;
      }
    },
    "form.TypeEntityBank": {
      handler(newVal) {
        this.$nextTick(() => {
          this.onBankChanged(newVal);
          if (newVal != 2) {
            this.NumCorrelative = false;
          }

          if (!this.isHydrating) {
            const accounts = this.existingAccountsForSelectedBank;
            if (accounts.length > 0) {
              const defaultAcc =
                accounts.find((a) => a.BatIsDefault === 1) || accounts[0];
              this.selectedExistingAccountId = defaultAcc.BatID;
              this.onExistingAccountSelected(defaultAcc.BatID);
            } else {
              this.selectedExistingAccountId = 0;
              this.onExistingAccountSelected(0);
            }
          } else {
            this.preselectExistingAccount();
          }
        });
      },
    },
    "form.TypePensionRegimen": {
      handler(newVal) {
        if (!this.isHydrating && newVal) {
          const prev = this.financialData?.Pensions?.find(
            (p) => p.TypePensionRegimen === newVal && p.SecStatus === 1,
          );
          if (prev) {
            this.form.CUSPP = prev.CUSPP;
            this.form.PsnAttach = prev.PsnAttach;
          } else {
            this.form.CUSPP = "";
            this.form.PsnAttach = null;
          }
        }
      },
    },
    "form.TypeHealthRegimen": {
      handler(newVal) {
        if (!this.isHydrating && newVal) {
          const prev = this.financialData?.Healths?.find(
            (h) => h.TypeHealthRegimen === newVal && h.SecStatus === 1,
          );
          if (prev) {
            this.form.TypeCompany = prev.TypeCompany;
          } else {
            this.form.TypeCompany = null;
          }
        }
      },
    },
    "form.TypeContract": {
      handler(newVal) {
        this.$nextTick(() => {
          this.onTypeContractChanged(newVal);
        });
      },
    },
    "form.BatStatus": {
      handler(newVal) {
        if (newVal == 3) {
          this.NumCorrelative = false;
          if (this.isBcpBank) {
            this.form.BatAccountNumber = "";
          }
        } else if (newVal == 1 && this.isBcpBank) {
          this.NumCorrelative = true;
        } else if (newVal == 2 && this.isBcpBank) {
          this.NumCorrelative = false;
        }
      },
    },
  },

  mounted() {
    this.loadQuadrille();
    this.loadEstablishments();
    this.loadSystemParameters();
  },

  methods: {
    onAdmissionDateChange() {
      if (
        this.form.AgtBeginDate &&
        this.form.AgtBeginDate < this.form.AgtAdmissionDate
      ) {
        this.form.AgtBeginDate = this.form.AgtAdmissionDate;
      }
    },
    emptyForm(data = {}) {
      const todayStr = new Date().toISOString().split("T")[0];
      const sanitizeId = (val, defaultVal = null) => {
        if (
          val === undefined ||
          val === null ||
          val === "" ||
          val === 0 ||
          val === "0"
        ) {
          return defaultVal;
        }
        const num = Number(val);
        return isNaN(num) || num <= 0 ? defaultVal : num;
      };
      console.log("dataaaaaaaaaaaaaaaxddxxddd", data);
      return {
        AgtID: data.AgtID || 0,
        WkrID: sanitizeId(data.WkrID, 0),
        NtpID: sanitizeId(data.NtpID, 0),
        TypePayroll: sanitizeId(data.TypePayroll, 1),
        AreID: sanitizeId(data.AreID, null),
        AreName: data.AreName || null,
        CceID: sanitizeId(data.CceID, null),
        CceName: data.CceName || null,
        BslID: sanitizeId(data.BslID, null),
        BslName: data.BslName || null,
        PstID: sanitizeId(data.PstID, null),
        PstName: data.PstName || null,
        PstIsBascCritic: data.PstIsBascCritic || 0,
        PstIsBascCriticName: data.PstIsBascCriticName || null,
        GngID: sanitizeId(data.GngID, 1),
        TypeContract: sanitizeId(data.TypeContract, 11),
        TypeCategory: sanitizeId(data.TypeCategory, 1),
        TypeFormatLabor: sanitizeId(data.TypeFormatLabor, null),
        TypeWorker: sanitizeId(
          data.TypeWorker !== undefined && data.TypeWorker !== null
            ? data.TypeWorker
            : data.TypePosition,
          null,
        ),
        TypeLaborRegime: sanitizeId(data.TypeLaborRegime, 3),
        TypeOccupationalCategory: sanitizeId(
          data.TypeOccupationalCategory !== undefined &&
            data.TypeOccupationalCategory !== null
            ? data.TypeOccupationalCategory
            : data.TypeWorker || data.TypePosition,
          4,
        ),
        EstID: sanitizeId(
          data.EstID,
          this.establishmentsList && this.establishmentsList.length > 0
            ? this.establishmentsList[0].EstID
            : 1,
        ),
        AgtIsSCTR: !!data.AgtIsSCTR,
        AgtScrtBeginDate:
          data.AgtScrtBeginDate ||
          data.AgtSctrBeginDate ||
          data.SctrBeginDate ||
          data.AgtBeginDate ||
          todayStr,
        TypeCivilStatus: sanitizeId(data.TypeCivilStatus, 1),
        AgtChildrenQuantity: data.AgtChildrenQuantity || 0,
        TypeTurno: sanitizeId(data.TypeTurno, 1),
        TypeSituation: sanitizeId(data.TypeSituation, 2),
        TypeSpecialSituation: sanitizeId(data.TypeSpecialSituation, 1),
        AgtMonthsProbation:
          data.AgtMonthsProbation !== undefined &&
          data.AgtMonthsProbation !== null
            ? data.AgtMonthsProbation
            : this.isNewWorker
            ? 3
            : 0,
        AgtSalary: data.AgtSalary || data.ReqEstBudget || null,
        CmpID: sanitizeId(data.CmpID, null),
        AgtMaximunDay:
          data.AgtMaximunDay !== undefined && data.AgtMaximunDay !== null
            ? data.AgtMaximunDay
            : true,
        AgtAdmissionDate: data.AgtAdmissionDate
          ? String(data.AgtAdmissionDate).substring(0, 10)
          : todayStr,
        AgtBeginDate: data.AgtBeginDate
          ? String(data.AgtBeginDate).substring(0, 10)
          : todayStr,
        AgtEndDate: this.renovation
          ? data.AgtEndDate
            ? String(data.AgtEndDate).substring(0, 10)
            : data.AgtBeginDate
            ? String(data.AgtBeginDate).substring(0, 10)
            : todayStr
          : data.AgtEndDate
          ? String(data.AgtEndDate).substring(0, 10)
          : data._prevAgtEndDate
          ? String(data._prevAgtEndDate).substring(0, 10)
          : null,
        AgtTerminationDate: data.AgtTerminationDate
          ? String(data.AgtTerminationDate).substring(0, 10)
          : null,
        AgtServiceDescription: data.AgtServiceDescription || null,
        TypeTermination: sanitizeId(data.TypeTermination, null),
        _isSeason: data._isSeason !== undefined ? !!data._isSeason : false,
        _isIndeterminate:
          data._isIndeterminate !== undefined ? !!data._isIndeterminate : false,
        _endDateLocked: !this.extension,
        _prevAgtEndDate: data._prevAgtEndDate
          ? String(data._prevAgtEndDate).substring(0, 10)
          : data.AgtEndDate
          ? String(data.AgtEndDate).substring(0, 10)
          : null,
        CmpEndDate: data.CmpEndDate
          ? String(data.CmpEndDate).substring(0, 10)
          : null,
        ReqID: this.renovation ? null : sanitizeId(data.ReqID, null),
        AgtRenovation: this.renovation ? 0 : sanitizeId(data.AgtRenovation, 0),
        AgtRenewedFromID: sanitizeId(data.AgtRenewedFromID, null),

        // Pensión (HmnPension)
        PsnID: sanitizeId(data.PsnID, null),
        TypePensionRegimen: sanitizeId(data.TypePensionRegimen, null),
        CUSPP: data.CUSPP || null,
        PsnAttach: data.PsnAttach || null,
        AgtPsnBeginDate: data.AgtPsnBeginDate || data.AgtBeginDate || todayStr,

        // Salud (HmnHealth)
        HthID: sanitizeId(data.HthID, null),
        TypeHealthRegimen: sanitizeId(data.TypeHealthRegimen, null),
        TypeCompany: sanitizeId(data.TypeCompany, null),
        AgtHthBeginDate: data.AgtHthBeginDate || data.AgtBeginDate || todayStr,

        // Banco (HmnBankAccount - Remuneración)
        BatID: sanitizeId(data.BatID, null),
        TypeEntityBank: sanitizeId(data.TypeEntityBank, 2),
        TypePay: sanitizeId(data.TypePay, 1),
        TypeMoney: sanitizeId(data.TypeMoney, 1),
        BatStatus: sanitizeId(data.BatStatus, 1),
        BatAccountNumber: data.BatAccountNumber || null,
        BatCCI: data.BatCCI || null,
        AgtBatBeginDate: data.AgtBatBeginDate || data.AgtBeginDate || todayStr,

        // Banco (HmnBankAccount - CTS)
        BatID_CTS: sanitizeId(data.BatID_CTS || data.BatCtsID, null),
        TypeEntityBank_CTS: sanitizeId(data.TypeEntityBank_CTS || data.TypeEntityBankCts, 2),
        TypePay_CTS: sanitizeId(data.TypePay_CTS || data.TypePayCts, 2),
        TypeMoney_CTS: sanitizeId(data.TypeMoney_CTS || data.TypeMoneyCts, 1),
        BatStatus_CTS: sanitizeId(data.BatStatus_CTS || data.BatStatusCts, 1),
        BatAccountNumber_CTS: data.BatAccountNumber_CTS || data.BatAccountNumberCts || null,
        BatCCI_CTS: data.BatCCI_CTS || data.BatCciCts || null,
        AgtBatBeginDate_CTS: data.AgtBatBeginDate_CTS || data.AgtBeginDate || todayStr,
      };
    },

    async resetForm(data = {}) {
      this.isHydrating = true;
      this.isUserChangingCampaign = false;
      this.form = this.emptyForm(data);
      this.previousCmpID = this.form.CmpID;
      this.registerCessation = false;
      this.estIsSCTRRisk = false;
      this.checkEstablishmentRisk(this.form.EstID);
      await this.loadWorkerFinancialData(this.form.WkrID);
      this.$nextTick(() => {
        this.isHydrating = false;
        this.checkVacancyQuota();
      });
    },

    async hydrateForm(data) {
      if (!data) {
        await this.resetForm();
        return;
      }

      if (data._isNew) {
        await this.resetForm(data);
        return;
      }

      this.isHydrating = true;
      this.isUserChangingCampaign = false;
      const clean = this.emptyForm(data);
      this.previousCmpID = clean.CmpID;

      if (data.initialModificationScope) {
        this.modificationScope = data.initialModificationScope;
      }

      this.form = clean;
      if (
        this.extension &&
        !this.form.AgtEndDate &&
        this.form._prevAgtEndDate
      ) {
        this.form.AgtEndDate = this.form._prevAgtEndDate;
      }
      if (this.renovation && !this.form.AgtEndDate && this.form.AgtBeginDate) {
        this.form.AgtEndDate = String(this.form.AgtBeginDate).substring(0, 10);
      }
      if (!this.form.EstID) {
        this.form.EstID =
          this.establishmentsList && this.establishmentsList.length > 0
            ? this.establishmentsList[0].EstID
            : 1;
      }
      this.checkEstablishmentRisk(this.form.EstID);
      this.checkAreaSctrRequirement(this.form.AreID);
      if (!this.isSctrMandatory) {
        this.form.AgtIsSCTR = !!data.AgtIsSCTR;
      }
      this.$emit("sctrChanged", this.form.AgtIsSCTR);

      if (data.AgtMaximunDay === undefined || data.AgtMaximunDay === null) {
        this.form.AgtMaximunDay = true;
      }
      this.registerCessation = !!(
        data.AgtTerminationDate ||
        data.TypeTermination ||
        data.AgtStatus == 2
      );
      await this.loadWorkerFinancialData(this.form.WkrID);
      if (this.form.PstID) {
        this.onPositionChanged(this.form.PstID);
      }
      this.$nextTick(() => {
        if (this.form.TypeEntityBank) {
          this.onBankChanged(this.form.TypeEntityBank);
        }
        this.isHydrating = false;
      });
    },

    addDaysToStr(dateStr, days = 1) {
      if (!dateStr || dateStr.length < 10) return null;
      const parts = dateStr.substring(0, 10).split("-");
      if (parts.length !== 3) return null;
      const d = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
      );
      d.setDate(d.getDate() + days);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    getPrevPsnDate() {
      if (!this.datawork) return null;
      let raw = this.datawork.AgtPsnBeginDate || this.datawork.AmsPsnBeginDate;
      if (!raw && this.financialData?.Pensions?.length > 0) {
        const active =
          this.financialData.Pensions.find((p) => p.PsnIsDefault === 1) ||
          this.financialData.Pensions[0];
        if (active)
          raw =
            active.AmsPsnBeginDate || active.PsnBeginDate || active.SecCreate;
      }
      if (!raw) raw = this.datawork.AgtBeginDate;
      return raw ? String(raw).substring(0, 10) : null;
    },

    getPrevHthDate() {
      if (!this.datawork) return null;
      let raw = this.datawork.AgtHthBeginDate || this.datawork.AmsHthBeginDate;
      if (!raw && this.financialData?.Healths?.length > 0) {
        const active =
          this.financialData.Healths.find((h) => h.HthIsDefault === 1) ||
          this.financialData.Healths[0];
        if (active)
          raw =
            active.AmsHthBeginDate || active.HthBeginDate || active.SecCreate;
      }
      if (!raw) raw = this.datawork.AgtBeginDate;
      return raw ? String(raw).substring(0, 10) : null;
    },

    setModificationScope(scope) {
      if (scope) {
        this.modificationScope = scope;
        if (scope === "MOD_PENSION" || scope === "MOD_ALL") {
          const prev = this.getPrevPsnDate();
          if (prev && prev !== "null" && this.form.AgtPsnBeginDate <= prev) {
            const nextDay = this.addDaysToStr(prev, 1);
            if (nextDay) this.form.AgtPsnBeginDate = nextDay;
          }
        }
        if (scope === "MOD_SALUD" || scope === "MOD_ALL") {
          const prev = this.getPrevHthDate();
          if (prev && prev !== "null" && this.form.AgtHthBeginDate <= prev) {
            const nextDay = this.addDaysToStr(prev, 1);
            if (nextDay) this.form.AgtHthBeginDate = nextDay;
          }
        }
      }
    },

    async loadWorkerFinancialData(wkrID) {
      if (!wkrID) {
        this.financialData = { BankAccounts: [], Pensions: [], Healths: [] };
        this.selectedExistingAccountId = null;
        this.selectedExistingCtsAccountId = null;
        this.selectedExistingPensionId = null;
        this.selectedExistingHealthId = null;
        return;
      }
      const res = await _sCttoService.workerFinancialData(
        wkrID,
        this.$fun.getUserID(),
      );
      if (res?.status === 200 && res.data) {
        this.financialData = res.data;
        this.preselectFinancialRecords();
      }
    },

    preselectFinancialRecords() {
      this.preselectExistingAccount();
      this.preselectExistingCtsAccount();
      this.preselectExistingPension();
      this.preselectExistingHealth();
    },

    preselectExistingAccount() {
      if (this.financialData?.BankAccounts?.length > 0) {
        let acc = null;
        if (this.form.BatID) {
          acc = this.financialData.BankAccounts.find(
            (a) => a.BatID === this.form.BatID,
          );
        }
        if (!acc && this.form.BatAccountNumber) {
          acc = this.financialData.BankAccounts.find(
            (a) => a.BatAccountNumber === this.form.BatAccountNumber,
          );
        }
        if (!acc) {
          const haberesAccounts = this.financialData.BankAccounts.filter(
            (a) => a.TypePay === 1 || !a.TypePay,
          );
          acc =
            haberesAccounts.find(
              (a) =>
                a.TypeEntityBank === this.form.TypeEntityBank &&
                a.BatIsDefault === 1,
            ) ||
            haberesAccounts.find(
              (a) => a.TypeEntityBank === this.form.TypeEntityBank,
            ) ||
            haberesAccounts.find((a) => a.BatIsDefault === 1) ||
            haberesAccounts[0] ||
            null;
        }
        if (acc) {
          this.selectedExistingAccountId = acc.BatID;
          this.form.BatID = acc.BatID;
          if (acc.TypeEntityBank) this.form.TypeEntityBank = acc.TypeEntityBank;
          if (acc.BatAccountNumber)
            this.form.BatAccountNumber = acc.BatAccountNumber;
          if (acc.BatCCI) this.form.BatCCI = acc.BatCCI;
          if (acc.TypePay) this.form.TypePay = acc.TypePay;
          if (acc.TypeMoney) this.form.TypeMoney = acc.TypeMoney;
          if (acc.BatStatus) this.form.BatStatus = acc.BatStatus;
        } else {
          this.selectedExistingAccountId = 0;
        }
      } else {
        this.selectedExistingAccountId = 0;
      }
    },

    preselectExistingCtsAccount() {
      if (this.financialData?.BankAccounts?.length > 0) {
        let acc = null;
        if (this.form.BatID_CTS) {
          acc = this.financialData.BankAccounts.find(
            (a) => a.BatID === this.form.BatID_CTS,
          );
        }
        if (!acc && this.form.BatAccountNumber_CTS) {
          acc = this.financialData.BankAccounts.find(
            (a) => a.BatAccountNumber === this.form.BatAccountNumber_CTS,
          );
        }
        if (!acc) {
          const ctsAccounts = this.financialData.BankAccounts.filter(
            (a) => a.TypePay === 2 || a.TypeAccountPurpose === 2,
          );
          acc =
            ctsAccounts.find(
              (a) => a.TypeEntityBank === this.form.TypeEntityBank_CTS,
            ) ||
            ctsAccounts[0] ||
            null;
        }
        if (acc) {
          this.selectedExistingCtsAccountId = acc.BatID;
          this.form.BatID_CTS = acc.BatID;
          if (acc.TypeEntityBank) this.form.TypeEntityBank_CTS = acc.TypeEntityBank;
          if (acc.BatAccountNumber)
            this.form.BatAccountNumber_CTS = acc.BatAccountNumber;
          if (acc.BatCCI) this.form.BatCCI_CTS = acc.BatCCI;
          if (acc.TypePay) this.form.TypePay_CTS = acc.TypePay;
          if (acc.TypeMoney) this.form.TypeMoney_CTS = acc.TypeMoney;
          if (acc.BatStatus) this.form.BatStatus_CTS = acc.BatStatus;
        } else {
          this.selectedExistingCtsAccountId = 0;
        }
      } else {
        this.selectedExistingCtsAccountId = 0;
      }
    },

    preselectExistingPension() {
      if (this.financialData?.Pensions?.length > 0) {
        let p = null;
        if (this.form.PsnID) {
          p = this.financialData.Pensions.find(
            (x) => x.PsnID === this.form.PsnID,
          );
        }
        if (!p && this.form.TypePensionRegimen) {
          p = this.financialData.Pensions.find(
            (x) => x.TypePensionRegimen === this.form.TypePensionRegimen,
          );
        }
        if (!p) {
          p =
            this.financialData.Pensions.find((x) => x.PsnIsDefault === 1) ||
            this.financialData.Pensions[0];
        }
        if (p) {
          this.selectedExistingPensionId = p.PsnID;
          this.form.PsnID = p.PsnID;
          if (p.TypePensionRegimen)
            this.form.TypePensionRegimen = p.TypePensionRegimen;
          if (p.CUSPP) this.form.CUSPP = p.CUSPP;
          if (p.PsnAttach) this.form.PsnAttach = p.PsnAttach;
        } else {
          this.selectedExistingPensionId = 0;
        }
      } else {
        this.selectedExistingPensionId = 0;
      }
    },

    preselectExistingHealth() {
      if (this.financialData?.Healths?.length > 0) {
        let h = null;
        if (this.form.HthID) {
          h = this.financialData.Healths.find(
            (x) => x.HthID === this.form.HthID,
          );
        }
        if (!h && this.form.TypeHealthRegimen) {
          h = this.financialData.Healths.find(
            (x) => x.TypeHealthRegimen === this.form.TypeHealthRegimen,
          );
        }
        if (!h) {
          h =
            this.financialData.Healths.find((x) => x.HthIsDefault === 1) ||
            this.financialData.Healths[0];
        }
        if (h) {
          this.selectedExistingHealthId = h.HthID;
          this.form.HthID = h.HthID;
          if (h.TypeHealthRegimen)
            this.form.TypeHealthRegimen = h.TypeHealthRegimen;
          if (h.TypeCompany) this.form.TypeCompany = h.TypeCompany;
        } else {
          this.selectedExistingHealthId = 0;
        }
      } else {
        this.selectedExistingHealthId = 0;
      }
    },

    async loadEstablishments() {
      try {
        const res = await ServiceEstablishment.list(this.$fun.getUserID());
        if (
          res?.status === 200 &&
          Array.isArray(res.data) &&
          res.data.length > 0
        ) {
          this.establishmentsList = res.data;
          const firstEstID =
            res.data[0].EstID || res.data[0].id || res.data[0].value;
          if (!this.form.EstID) {
            this.form.EstID = firstEstID;
          }
          this.checkEstablishmentRisk(this.form.EstID);
        }
      } catch (e) {
        console.warn("Error cargando establecimientos:", e);
      }
    },

    async loadSystemParameters() {
      try {
        const userId = this.$fun.getUserID();
        const [resSctr, resQuad] = await Promise.all([
          this.loadParamByName("SctrMandatoryAreaIds_RRHH", userId),
          this.loadParamByName("RequireQuadrillePstIds_RRHH", userId),
        ]);

        if (resSctr !== null && resSctr !== undefined) {
          this.fetchedSctrAreaIds = resSctr;
        }

        if (resQuad !== null && resQuad !== undefined) {
          this.fetchedQuadrillePstIds = resQuad;
        }

        this.$nextTick(() => {
          this.checkAreaSctrRequirement(this.form.AreID);
        });
      } catch (e) {
        console.warn(
          "Error cargando parámetros generales en ContractLaborForm:",
          e,
        );
      }
    },

    async loadParamByName(prmName, userId) {
      try {
        let res = await _sGenParam
          .search({ PrmName: prmName }, userId)
          .catch(() => null);
        if (res && res.status === 200 && res.data) {
          const item = Array.isArray(res.data) ? res.data[0] : res.data;
          if (
            item &&
            item.PrmValue !== undefined &&
            item.PrmValue !== null &&
            item.PrmValue !== ""
          ) {
            return item.PrmValue;
          }
        }

        res = await _sGenParam
          .listoptions({ Search: prmName }, userId)
          .catch(() => null);
        if (
          res &&
          res.status === 200 &&
          Array.isArray(res.data) &&
          res.data.length > 0
        ) {
          const item =
            res.data.find((x) => x.PrmName === prmName) || res.data[0];
          if (item && item.PrmValue !== undefined && item.PrmValue !== null) {
            return item.PrmValue;
          }
        }
      } catch (e) {
        console.error(`Error consultando parámetro ${prmName}:`, e);
      }
      return null;
    },

    parseIdsToArray(val, fallback = []) {
      if (val === undefined || val === null || val === "") return fallback;
      if (Array.isArray(val))
        return val.map((x) => Number(x)).filter((x) => !isNaN(x) && x > 0);
      if (typeof val === "number") return [val];
      if (typeof val === "string") {
        return val
          .split(/[,;\s]+/)
          .map((x) => Number(x.trim()))
          .filter((x) => !isNaN(x) && x > 0);
      }
      return fallback;
    },

    checkAreaSctrRequirement(areID) {
      const targetAreID = areID || this.form.AreID;
      if (!targetAreID) return;
      if (this.isSctrMandatory) {
        this.form.AgtIsSCTR = true;
        this.$emit("sctrChanged", true);
      }
    },

    onAreaChanged(val) {
      let areID =
        val && typeof val === "object"
          ? val.AreID || val.id
          : val || this.form.AreID;
      if (areID) {
        this.form.AreID = areID;
      }
      this.$nextTick(() => {
        this.checkAreaSctrRequirement(areID);
      });
    },

    checkEstablishmentRisk(estID) {
      if (!estID) {
        this.estIsSCTRRisk = false;
        this.$emit("sctrRiskChanged", false);
        if (this.isAreaSctrMandatory) {
          this.form.AgtIsSCTR = true;
          this.$emit("sctrChanged", true);
        }
        return;
      }
      const est = this.establishmentsList.find((x) => x.EstID == estID);
      if (est) {
        const isRisk = !!(
          est.EstIsSCTRRisk === 1 ||
          est.EstIsSCTRRisk === true ||
          est.EstIsSCTRRisk === "1"
        );
        this.estIsSCTRRisk = isRisk;
        this.$emit("sctrRiskChanged", isRisk);
        if (this.isSctrMandatory) {
          this.form.AgtIsSCTR = true;
          this.$emit("sctrChanged", true);
        }
      } else {
        this.estIsSCTRRisk = false;
        this.$emit("sctrRiskChanged", false);
        if (this.isAreaSctrMandatory) {
          this.form.AgtIsSCTR = true;
          this.$emit("sctrChanged", true);
        }
      }
    },

    async loadQuadrille() {
      let obj = {
        SecStatus: 1,
      };
      _sQuadriService.list(obj, this.$fun.getUserID()).then((resp) => {
        if (resp.status == 200) {
          this.quadrille = resp.data;
        }
      });
    },

    async onCorrelativeChange(val) {
      if (!val) {
        this.form.BatAccountNumber = "";
        return;
      }
      if (!this.isBcpBank) {
        this.NumCorrelative = false;
        return;
      }
      this.generatingCorrelative = true;
      try {
        const length = this.bankAccountLength || 14;
        const res = await _sWorkertService.BankAccountCorrelative(
          this.$fun.getUserID(),
          length,
        );
        if (res?.data) {
          this.form.BatAccountNumber = String(res.data);
          this.$fun.alert(
            "Número de cuenta correlativo generado: " + res.data,
            "success",
          );
        }
      } catch (e) {
        console.error("Error generando correlativo:", e);
        this.NumCorrelative = false;
        this.$fun.alert(e.Message ?? e.data.Message, "error");
      } finally {
        this.generatingCorrelative = false;
      }
    },
    generateCorrelativeAccount() {
      return this.onCorrelativeChange(true);
    },

    onExistingAccountSelected(batID) {
      if (batID === 0) {
        this.form.BatID = 0;
        this.form.BatAccountNumber = "";
        this.form.BatCCI = "";
        this.form.TypePay = 1;
        this.form.TypeMoney = 1;
        this.form.BatStatus = 1;
        this.NumCorrelative = false;
      } else {
        const acc = this.financialData?.BankAccounts?.find(
          (a) => a.BatID === batID,
        );
        if (acc) {
          this.form.BatID = acc.BatID;
          this.form.TypeEntityBank = acc.TypeEntityBank;
          this.form.BatAccountNumber = acc.BatAccountNumber;
          this.form.BatCCI = acc.BatCCI;
          this.form.TypePay = acc.TypePay;
          this.form.TypeMoney = acc.TypeMoney;
          this.form.BatStatus = acc.BatStatus;
          if (this.isBcpBank && acc.BatStatus == 1) {
            this.NumCorrelative = true;
          } else {
            this.NumCorrelative = false;
          }
        }
      }
    },

    onExistingCtsAccountSelected(batID) {
      if (batID === 0) {
        this.form.BatID_CTS = 0;
        this.form.BatAccountNumber_CTS = "";
        this.form.BatCCI_CTS = "";
        this.form.TypePay_CTS = 2;
        this.form.TypeMoney_CTS = 1;
        this.form.BatStatus_CTS = 1;
      } else {
        const acc = this.financialData?.BankAccounts?.find(
          (a) => a.BatID === batID,
        );
        if (acc) {
          this.form.BatID_CTS = acc.BatID;
          this.form.TypeEntityBank_CTS = acc.TypeEntityBank;
          this.form.BatAccountNumber_CTS = acc.BatAccountNumber;
          this.form.BatCCI_CTS = acc.BatCCI;
          this.form.TypePay_CTS = acc.TypePay || 2;
          this.form.TypeMoney_CTS = acc.TypeMoney;
          this.form.BatStatus_CTS = acc.BatStatus;
        }
      }
    },

    copyFromHaberesToCts() {
      this.form.TypeEntityBank_CTS = this.form.TypeEntityBank;
      this.form.TypePay_CTS = 2;
      this.form.TypeMoney_CTS = this.form.TypeMoney;
      this.form.BatStatus_CTS = this.form.BatStatus;
      this.form.BatAccountNumber_CTS = this.form.BatAccountNumber;
      this.form.BatCCI_CTS = this.form.BatCCI;
      this.$nextTick(() => {
        this.onCtsBankChanged();
      });
      this.$fun.alert("Datos de la cuenta de haberes copiados a la cuenta CTS", "info");
    },

    onCtsBankChanged(val, item) {
      const ref = this.$refs.typeEntityBankCtsRef;
      const items =
        ref?.items && ref.items.length > 0
          ? ref.items
          : ref?.$refs?.sgeneric?.items || [];

      const selected =
        item && (item.DedHelper !== undefined || item.dedHelper !== undefined)
          ? item
          : items.find((x) => x.DedValue === this.form.TypeEntityBank_CTS) || null;

      const rawLength = parseInt(
        selected?.DedHelper !== undefined
          ? selected.DedHelper
          : selected?.dedHelper,
        10,
      );
      this.bankAccountLength_CTS = Number.isNaN(rawLength) ? null : rawLength;

      this.accountPrefixRequired_CTS =
        selected?.DedHelper2 !== undefined
          ? selected.DedHelper2
          : selected?.dedHelper2 || null;

      this.bankDedAbbreviation_CTS =
        selected?.DedAbbreviation !== undefined
          ? selected.DedAbbreviation
          : selected?.dedAbbreviation !== undefined
          ? selected.dedAbbreviation
          : null;

      const rawCciLen = parseInt(
        selected?.DedFormat !== undefined
          ? selected.DedFormat
          : selected?.dedFormat,
        10,
      );
      this.bankCciLength_CTS =
        Number.isNaN(rawCciLen) || rawCciLen <= 0 ? 20 : rawCciLen;
    },

    onExistingPensionSelected(psnID) {
      if (psnID === 0) {
        this.form.PsnID = 0;
        this.form.TypePensionRegimen = null;
        this.form.CUSPP = "";
        this.form.PsnAttach = null;
        const prev = this.getPrevPsnDate();
        if (prev && prev !== "null") {
          const nextDay = this.addDaysToStr(prev, 1);
          if (nextDay) this.form.AgtPsnBeginDate = nextDay;
        }
      } else {
        const item = this.financialData?.Pensions?.find(
          (p) => p.PsnID === psnID,
        );
        if (item) {
          this.form.PsnID = item.PsnID;
          this.form.TypePensionRegimen = item.TypePensionRegimen;
          this.form.CUSPP = item.CUSPP;
          this.form.PsnAttach = item.PsnAttach;
        }
      }
    },

    onExistingHealthSelected(hthID) {
      if (hthID === 0) {
        this.form.HthID = 0;
        this.form.TypeHealthRegimen = null;
        this.form.TypeCompany = null;
        const prev = this.getPrevHthDate();
        if (prev && prev !== "null") {
          const nextDay = this.addDaysToStr(prev, 1);
          if (nextDay) this.form.AgtHthBeginDate = nextDay;
        }
      } else {
        const item = this.financialData?.Healths?.find(
          (h) => h.HthID === hthID,
        );
        if (item) {
          this.form.HthID = item.HthID;
          this.form.TypeHealthRegimen = item.TypeHealthRegimen;
          this.form.TypeCompany = item.TypeCompany;
        }
      }
    },

    onBankChanged(val, item) {
      const ref = this.$refs.typeEntityBankRef;
      const items =
        ref?.items && ref.items.length > 0
          ? ref.items
          : ref?.$refs?.sgeneric?.items || [];

      const selected =
        item && (item.DedHelper !== undefined || item.dedHelper !== undefined)
          ? item
          : items.find((x) => x.DedValue === this.form.TypeEntityBank) || null;

      const rawLength = parseInt(
        selected?.DedHelper !== undefined
          ? selected.DedHelper
          : selected?.dedHelper,
        10,
      );
      this.bankAccountLength = Number.isNaN(rawLength) ? null : rawLength;

      this.accountPrefixRequired =
        selected?.DedHelper2 !== undefined
          ? selected.DedHelper2
          : selected?.dedHelper2 || null;

      this.bankDedAbbreviation =
        selected?.DedAbbreviation !== undefined
          ? selected.DedAbbreviation
          : selected?.dedAbbreviation !== undefined
          ? selected.dedAbbreviation
          : null;

      const rawCciLen = parseInt(
        selected?.DedFormat !== undefined
          ? selected.DedFormat
          : selected?.dedFormat,
        10,
      );
      this.bankCciLength =
        Number.isNaN(rawCciLen) || rawCciLen <= 0 ? 20 : rawCciLen;
    },

    onEstablishmentChanged(val) {
      let estID =
        val && typeof val === "object"
          ? val.EstID || val.id
          : val || this.form.EstID;
      if (estID) {
        this.form.EstID = estID;
      }
      this.checkEstablishmentRisk(this.form.EstID);
    },

    applyEstablishmentData(est) {
      if (!est) return;
      const isRisk = !!(
        est.EstIsSCTRRisk === 1 ||
        est.EstIsSCTRRisk === true ||
        est.EstIsSCTRRisk === "1"
      );
      this.estIsSCTRRisk = isRisk;
      this.$emit("sctrRiskChanged", isRisk);
      if (isRisk) {
        this.form.AgtIsSCTR = true;
        this.$emit("sctrChanged", true);
      }
    },

    onSctrChanged(val) {
      if (this.isSctrMandatory) {
        this.form.AgtIsSCTR = true;
        this.$emit("sctrChanged", true);
        const reason = this.isAreaSctrMandatory
          ? "El área seleccionada requiere seguro SCTR obligatorio por norma."
          : "El establecimiento seleccionado es de alto riesgo. El seguro SCTR es obligatorio por ley.";
        this.$fun.alert(reason, "warning");
        return;
      }
      this.$emit("sctrChanged", !!val);
    },

    onCategoryChanged(val) {
      if (!this.isModalidadFormativa) {
        this.form.TypeFormatLabor = null;
      }
      // console.log("onCategoryChanged", val);

      this.$emit("categoryChanged", val);
    },

    onTypeContractItemsLoaded() {
      if (this.form.TypeContract) {
        this.onTypeContractChanged(this.form.TypeContract);
      }
    },

    onTypeContractChanged(val, item) {
      const rawVal =
        typeof val === "object" && val
          ? val.DedValue || val.GenID || val.value
          : val || this.form.TypeContract;

      let selectedItem = item;
      if (!selectedItem || typeof selectedItem !== "object") {
        const ref = this.$refs.typeContractRef;
        const items =
          ref?.items && ref.items.length > 0
            ? ref.items
            : ref?.$refs?.sgeneric?.items || [];
        selectedItem = items.find(
          (x) =>
            x.DedValue == rawVal ||
            x.GenID == rawVal ||
            x.value == rawVal ||
            x.id == rawVal ||
            x.TypeContract == rawVal,
        );
      }

      console.log(
        "Resolved selectedItem for TypeContract:",
        rawVal,
        selectedItem,
      );

      let helper2 =
        selectedItem && selectedItem.DedHelper2 !== undefined
          ? selectedItem.DedHelper2
          : selectedItem && selectedItem.dedHelper2 !== undefined
          ? selectedItem.dedHelper2
          : null;

      const desc = (
        selectedItem?.DedDescription ||
        selectedItem?.text ||
        ""
      ).toUpperCase();

      if (helper2 === "0" || helper2 === 0 || desc.includes("TEMPORADA")) {
        // Temporada: Fecha de fin tomada de la campaña y bloqueada para edición directa
        this.form._isSeason = true;
        this.form._endDateLocked = !this.extension;
        this.form._isIndeterminate = false;
        if (
          this.form.AgtMonthsProbation === undefined ||
          this.form.AgtMonthsProbation === null
        ) {
          this.form.AgtMonthsProbation = 3;
        }
        this.form.TypeSituation = 2;

        const isExistingContract =
          (this.form.AgtID && Number(this.form.AgtID) > 0) ||
          this.isModification ||
          this.extension ||
          this.renovation;

        if (
          this.form.CmpEndDate &&
          !this.extension &&
          !isExistingContract &&
          !this.isHydrating &&
          !this.form.AgtEndDate
        ) {
          this.form.AgtEndDate = this.form.CmpEndDate;
        }
        console.log("Contrato de Temporada detectado (helper2=0):", this.form);
      } else if (
        helper2 === "2" ||
        helper2 === 2 ||
        desc.includes("INDETERMINADO") ||
        desc.includes("INDEFINIDO")
      ) {
        // Sin fecha de termino (Indeterminado)
        this.form._isSeason = false;
        this.form._endDateLocked = true;
        this.form._isIndeterminate = true;
        if (this.form._isNew && !this.extension) {
          this.form.AgtEndDate = null;
        }
        this.form.TypeSituation = 1;
      } else {
        // Plazo Fijo / Otros con fecha término editable
        this.form._isSeason = false;
        this.form._endDateLocked = false;
        this.form._isIndeterminate = false;
        this.form.TypeSituation = 2;
      }
    },

    onCampaignSelected(val) {
      const newCmpID =
        val && typeof val === "object"
          ? val.GenID || val.CmpID || val.id
          : val;
      if (!this.isHydrating && newCmpID && newCmpID !== this.previousCmpID) {
        this.isUserChangingCampaign = true;
        this.previousCmpID = newCmpID;
      }
      this.$nextTick(() => {
        this.checkVacancyQuota();
      });
    },

    onCampaignDatesLoaded(dates) {
      if (dates) {
        const beginDate = dates.CmpBeginDate
          ? dates.CmpBeginDate.substring(0, 10)
          : dates.startDate
          ? dates.startDate.substring(0, 10)
          : null;
        const endDate = dates.CmpEndDate
          ? dates.CmpEndDate.substring(0, 10)
          : dates.endDate
          ? dates.endDate.substring(0, 10)
          : null;

        this.form.CmpEndDate = endDate;

        if (this.form._isIndeterminate) {
          this.form.AgtEndDate = null;
          return;
        }

        const isExistingContract =
          (this.form.AgtID && Number(this.form.AgtID) > 0) ||
          this.isModification ||
          this.extension ||
          this.renovation;

        // Si estamos hidratando el formulario, o es un contrato existente / ampliación / renovación / edición,
        // y el usuario NO cambió deliberadamente la campaña en este instante:
        if (
          this.isHydrating ||
          (isExistingContract && !this.isUserChangingCampaign)
        ) {
          if (!this.form.AgtBeginDate && beginDate && !isExistingContract) {
            this.form.AgtBeginDate = beginDate;
          }
          return;
        }

        // Si es un contrato nuevo desde cero o el usuario cambió deliberadamente la campaña desde el select:
        if (!this.form.AgtBeginDate && beginDate && !isExistingContract) {
          this.form.AgtBeginDate = beginDate;
        }

        // Asignar fecha fin si es temporada o si no tenía fecha fin fijada o si el usuario cambió la campaña
        if (
          (this.form._isSeason ||
            !this.form.AgtEndDate ||
            this.isUserChangingCampaign) &&
          !this.extension
        ) {
          if (endDate) {
            this.form.AgtEndDate = endDate;
          }
        }

        this.isUserChangingCampaign = false;
      }
    },

    getCriticityColor(val) {
      if (val == 1 || val == 0) return "grey lighten-3";
      if (val == 2) return "primary lighten-4";
      if (val == 3) return "orange lighten-4";
      return "red lighten-4";
    },
    getCriticityTextColor(val) {
      if (val == 1 || val == 0) return "grey darken-2";
      if (val == 2) return "primary darken-2";
      if (val == 3) return "orange darken-3";
      return "red darken-3";
    },

    onPositionChanged(item) {
      // console.log("onPositionChanged", item);
      // Extraemos el PstID real en caso venga como objeto (por si a caso)
      let pstID =
        typeof this.form.PstID === "object" && this.form.PstID
          ? this.form.PstID.GenID || this.form.PstID.PstID
          : this.form.PstID;

      // Forzamos que sea el ID numérico en el formulario
      if (typeof this.form.PstID === "object") {
        this.form.PstID = pstID;
      }

      if (!pstID) {
        this.form.PstName = null;
        this.form.PstIsBascCritic = 0;
        this.form.PstIsBascCriticName = null;
        return;
      }

      _sPositionService
        .searchByPstID(pstID, this.$fun.getUserID())
        .then((x) => {
          let position = x.data;
          console.log("cargo posicion = ", position, this.form);
          if (
            !this.form.TypeWorker ||
            !this.form.TypeLaborRegime ||
            !this.form.TypeOccupationalCategory
          ) {
            if (position.TypeLaborRegime && !this.form.TypeLaborRegime) {
              this.form.TypeLaborRegime = position.TypeLaborRegime;
              if (position.LaborRegimeText)
                this.form.LaborRegimeText = position.LaborRegimeText;
            }
            if (position.TypePosition && !this.form.TypeWorker) {
              this.form.TypeWorker = position.TypePosition;
              if (position.TypePositionName)
                this.form.TypeWorkerName = position.TypePositionName;
            }
            if (position.TypePosition && !this.form.TypeOccupationalCategory) {
              this.form.TypeOccupationalCategory = position.TypePosition;
            }
          }
          if (
            position.PstIsBascCritic !== undefined &&
            position.PstIsBascCritic !== null
          ) {
            this.form.PstIsBascCritic = position.PstIsBascCritic;
            this.form.PstIsBascCriticName = position.PstIsBascCriticName;
          }
          if (position.PstName) {
            this.form.PstName = position.PstName;
          } else if (item && item.GenDescription) {
            this.form.PstName = item.GenDescription;
          } else if (
            item &&
            Array.isArray(item) &&
            item[0] &&
            item[0].GenDescription
          ) {
            this.form.PstName = item[0].GenDescription;
          }

          this.$nextTick(() => {
            this.checkVacancyQuota();
            if (
              this.isQuadrilleRequired &&
              (!this.form.GngID || Number(this.form.GngID) <= 1)
            ) {
              this.$fun.alert(
                "El puesto seleccionado requiere asignar una cuadrilla mayor a 1.",
                "info",
              );
            }
          });
        });
    },

    async checkVacancyQuota() {
      if (
        this.renovation ||
        this.extension ||
        (this.form.AgtID && Number(this.form.AgtID) > 0)
      ) {
        this.vacancyQuotaInfo = null;
        return;
      }

      const rawCmp =
        typeof this.form.CmpID === "object" && this.form.CmpID
          ? this.form.CmpID.GenID || this.form.CmpID.CmpID || this.form.CmpID.id
          : this.form.CmpID;
      const rawPst =
        typeof this.form.PstID === "object" && this.form.PstID
          ? this.form.PstID.GenID || this.form.PstID.PstID || this.form.PstID.id
          : this.form.PstID;

      const cmpID = Number(rawCmp) || 0;
      const pstID = Number(rawPst) || 0;
      const reqID = this.form.ReqID ? Number(this.form.ReqID) : null;

      if (!cmpID || !pstID) {
        this.vacancyQuotaInfo = null;
        return;
      }

      this.isCheckingVacancy = true;
      try {
        const res = await _sCttoService.requirementVacancyStatus(
          {
            reqID: reqID,
            cmpID: cmpID,
            pstID: pstID,
            agtID: this.form.AgtID || 0,
          },
          this.$fun.getUserID()
        );

        const data = res.data;
        if (data && data.TotalVacancies > 0) {
          const isFull = data.CoveredVacancies >= data.TotalVacancies;
          this.vacancyQuotaInfo = {
            totalVacancies: data.TotalVacancies,
            coveredVacancies: data.CoveredVacancies,
            availableVacancies: data.TotalVacancies - data.CoveredVacancies,
            isQuotaFull: isFull,
          };
        } else {
          this.vacancyQuotaInfo = null;
        }
      } catch (err) {
        console.error("Error al consultar estado de vacantes:", err);
      } finally {
        this.isCheckingVacancy = false;
      }
    },

    async pullRequirementData(reqID) {
      const targetReqID = Number(reqID || this.form.ReqID);
      if (!targetReqID || isNaN(targetReqID)) {
        this.$fun.alert("No hay un ID de requerimiento válido", "warning");
        return;
      }
      this.isSyncingRequirement = true;
      try {
        const res = await _sHmnStaffingRequirement.byID(
          targetReqID,
          this.$fun.getUserID()
        );
        const req = res?.data;
        if (!req || !req.ReqID) {
          this.$fun.alert(
            `No se encontró el requerimiento #${targetReqID}`,
            "warning"
          );
          return;
        }

        this.form.ReqID = req.ReqID;

        // Actualizar Planilla y Régimen Laboral
        if (req.TypePayroll) {
          this.form.TypePayroll = Number(req.TypePayroll);
        }
        if (req.TypeLaborRegime) {
          this.form.TypeLaborRegime = Number(req.TypeLaborRegime);
          if (req.TypeLaborRegimeName) {
            this.form.LaborRegimeText = req.TypeLaborRegimeName;
          }
        }

        // Actualizar datos de estructura
        if (req.AreID) {
          this.form.AreID = Number(req.AreID);
          this.form.AreName = req.AreName;
        }
        if (req.ReqPstID || req.PstID) {
          this.form.PstID = Number(req.ReqPstID || req.PstID);
          this.form.PstName = req.PstName;
        }
        if (req.CceID) {
          this.form.CceID = Number(req.CceID);
          this.form.CceName = req.CceName;
        }
        if (req.CmpID) {
          this.form.CmpID = Number(req.CmpID);
          this.form.CmpName = req.CmpName;
        }
        if (req.ReqEndContract && !this.extension) {
          this.form.AgtEndDate = String(req.ReqEndContract).substring(0, 10);
        }

        // Actualizar sueldo si viene en el requerimiento
        if (req.ReqPayValue1) {
          const decSalary =
            this.$fun && this.$fun.decryptSalary
              ? this.$fun.decryptSalary(req.ReqPayValue1)
              : req.ReqPayValue1;
          if (decSalary && !isNaN(Number(decSalary)) && Number(decSalary) > 0) {
            this.form.AgtSalary = String(decSalary);
          }
        }

        this.$nextTick(() => {
          this.checkVacancyQuota();
        });

        this.$fun.alert(
          `Requerimiento #${req.ReqID} jalado con éxito: Planilla (${req.TypePayrollName || req.TypePayroll}), Régimen (${req.TypeLaborRegimeName || req.TypeLaborRegime})`,
          "success"
        );
      } catch (err) {
        console.error("Error al jalar datos de requerimiento:", err);
        this.$fun.alert(
          "Error al cargar datos del requerimiento #" + targetReqID,
          "error"
        );
      } finally {
        this.isSyncingRequirement = false;
      }
    },

    focusValidationField(refName, message) {
      this.$nextTick(() => {
        if (refName) {
          this.$fun.focusFieldError(this, refName, message);
          const el = this.$refs?.[refName]?.$el || this.$refs?.[refName];
          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      });
      this.$fun.alert(message, "warning");
    },

    validateData() {
      if (!this.form.TypePayroll) {
        this.focusValidationField("payrollRef", "Seleccione la Planilla.");
        return false;
      }
      if (!this.form.AreID) {
        this.focusValidationField("areaRef", "Seleccione el Área.");
        return false;
      }
      if (!this.form.PstID) {
        this.focusValidationField("positionRef", "Seleccione el Cargo.");
        return false;
      }
      if (
        this.vacancyQuotaInfo &&
        this.vacancyQuotaInfo.isQuotaFull &&
        !this.renovation &&
        !this.extension &&
        (!this.form.AgtID || Number(this.form.AgtID) === 0)
      ) {
        this.$fun.alert(
          `⛔ Vacantes Agotadas: El puesto seleccionado ya cuenta con ${this.vacancyQuotaInfo.coveredVacancies} contratados y cubrió el tope máximo de ${this.vacancyQuotaInfo.totalVacancies} vacantes para esta campaña. No es posible crear un nuevo contrato.`,
          "error"
        );
        return false;
      }
      if (
        this.isQuadrilleRequired &&
        (!this.form.GngID || Number(this.form.GngID) <= 1)
      ) {
        this.focusValidationField(
          "cuadrilleRef",
          "El puesto seleccionado requiere asignar una cuadrilla mayor a 1 (diferente a la cuadrilla por defecto 1).",
        );
        return false;
      }
      if (!this.form.TypeContract) {
        this.focusValidationField(
          "typeContractRef",
          "Seleccione el Tipo de Contrato.",
        );
        return false;
      }
      if (!this.form.TypeCategory) {
        this.focusValidationField(
          "typeCategoryRef",
          "Seleccione la Categoría.",
        );
        return false;
      }
      if (this.isModalidadFormativa && !this.form.TypeFormatLabor) {
        this.focusValidationField(
          "typeFormatLaborRef",
          "Para Modalidad Formativa debe seleccionar el tipo de modalidad.",
        );
        return false;
      }
      if (!this.form.TypeWorker) {
        this.focusValidationField(
          "typeWorkerRef",
          "Seleccione el Tipo de Trabajador.",
        );
        return false;
      }
      if (!this.form.TypeLaborRegime) {
        this.focusValidationField(
          "typeLaborRegimeRef",
          "Seleccione el Régimen Laboral.",
        );
        return false;
      }
      if (!this.form.TypeOccupationalCategory) {
        this.focusValidationField(
          "typeOccupationalCategoryRef",
          "Seleccione la Categoría Ocupacional.",
        );
        return false;
      }
      if (!this.form.EstID) {
        this.form.EstID = 1;
      }
      if (!this.form.TypeTurno) {
        this.focusValidationField("typeTurnoRef", "Seleccione el Turno.");
        return false;
      }
      if (!this.form.TypeSpecialSituation) {
        this.form.TypeSpecialSituation = 1;
      }
      if (!this.form.TypeSituation) {
        this.focusValidationField(
          "typeSituationRef",
          "Seleccione el Tipo de Costo.",
        );
        return false;
      }
      if (
        this.form.AgtMonthsProbation === null ||
        this.form.AgtMonthsProbation === undefined ||
        this.form.AgtMonthsProbation === ""
      ) {
        this.form.AgtMonthsProbation = 0;
      }
      if (!this.form.TypeCivilStatus) {
        this.focusValidationField(
          "typeCivilStatusRef",
          "Seleccione el Estado Civil.",
        );
        return false;
      }
      if (
        this.form.AgtChildrenQuantity === null ||
        this.form.AgtChildrenQuantity === undefined ||
        this.form.AgtChildrenQuantity === ""
      ) {
        this.form.AgtChildrenQuantity = 0;
      }

      // Validar Sueldo / Jornal
      const rawSalary = this.form.AgtSalary;
      let isValidSalary = false;

      if (rawSalary !== null && rawSalary !== undefined && rawSalary !== "") {
        if (typeof rawSalary === "number") {
          isValidSalary = rawSalary > 0;
        } else if (typeof rawSalary === "string") {
          const trimmedSalary = rawSalary.trim();
          if (trimmedSalary === "*****") {
            isValidSalary = true;
          } else {
            const directNum = parseFloat(trimmedSalary);
            if (
              !isNaN(directNum) &&
              isFinite(directNum) &&
              directNum > 0 &&
              String(directNum) === trimmedSalary
            ) {
              isValidSalary = true;
            } else {
              try {
                const decrypted = this.$fun?.decryptSalary
                  ? this.$fun.decryptSalary(trimmedSalary)
                  : "";
                const decNum = parseFloat(decrypted);
                if (!isNaN(decNum) && isFinite(decNum) && decNum > 0) {
                  isValidSalary = true;
                }
              } catch (e) {
                isValidSalary = false;
              }
            }
          }
        }
      }

      if (!isValidSalary) {
        this.focusValidationField(
          "salaryProtectedField",
          "El Sueldo/Jornal es obligatorio y debe ser mayor a 0.",
        );
        return false;
      }
      if (!this.form.CmpID) {
        this.focusValidationField("CmpIDRef", "Seleccione la Campaña.");
        return false;
      }
      if (!this.form.AgtAdmissionDate) {
        this.focusValidationField(
          "admissionDateRef",
          "Ingrese la Fecha de Ingreso.",
        );
        return false;
      }
      if (!this.form.AgtBeginDate) {
        this.focusValidationField(
          "beginDateRef",
          "Ingrese la Fecha de Inicio del Contrato.",
        );
        return false;
      }

      // Validar que la fecha de inicio no sea menor a la de ingreso
      const admissionDateStr = String(this.form.AgtAdmissionDate).substring(
        0,
        10,
      );
      const beginDateStr = String(this.form.AgtBeginDate).substring(0, 10);
      if (beginDateStr < admissionDateStr) {
        this.focusValidationField(
          "beginDateRef",
          "La Fecha de Inicio del Contrato no puede ser menor a la Fecha de Ingreso.",
        );
        return false;
      }

      // Validar fecha de fin de contrato
      if (!this.form._isIndeterminate) {
        if (!this.form.AgtEndDate) {
          this.focusValidationField(
            "endDateRef",
            "Para este tipo de contrato, la Fecha de Fin es obligatoria.",
          );
          return false;
        }

        const endDateStr = String(this.form.AgtEndDate).substring(0, 10);
        if (endDateStr < beginDateStr) {
          this.focusValidationField(
            "endDateRef",
            "La Fecha de Fin del Contrato no puede ser menor a la Fecha de Inicio.",
          );
          return false;
        }

        // Si es ampliación, la nueva fecha fin debe ser estrictamente mayor a la fecha fin anterior
        if (this.extension && this.form._prevAgtEndDate) {
          if (endDateStr <= this.form._prevAgtEndDate) {
            this.focusValidationField(
              "endDateRef",
              `Para una ampliación, la nueva Fecha de Fin (${endDateStr}) debe ser estrictamente posterior a la Fecha de Fin anterior (${this.form._prevAgtEndDate}).`,
            );
            return false;
          }
        }

        // Si es renovación, la fecha fin debe ser estrictamente mayor a la fecha de inicio
        if (this.renovation) {
          if (endDateStr <= beginDateStr) {
            this.focusValidationField(
              "endDateRef",
              `Para una renovación, la Fecha de Fin (${endDateStr}) debe ser estrictamente posterior a la Fecha de Inicio (${beginDateStr}).`,
            );
            return false;
          }
        }
      } else if (this.extension) {
        this.focusValidationField(
          "endDateRef",
          "Para una ampliación de contrato la Fecha de Fin es obligatoria.",
        );
        return false;
      } else if (this.renovation) {
        this.focusValidationField(
          "endDateRef",
          "Para una renovación de contrato la Fecha de Fin es obligatoria.",
        );
        return false;
      }

      // Validaciones Régimen Pensionario
      if (!this.isPensionDisabled) {
        if (!this.form.TypePensionRegimen) {
          this.focusValidationField(
            "typePensionRef",
            "Seleccione el Régimen Pensionario (AFP / ONP).",
          );
          return false;
        }

        // Si se seleccionó "+ Registrar nuevo régimen de pensión..."
        if (
          this.selectedExistingPensionId === 0 &&
          this.financialData?.Pensions?.length > 0
        ) {
          const alreadyExists = this.financialData.Pensions.some(
            (p) =>
              p.TypePensionRegimen == this.form.TypePensionRegimen &&
              p.SecStatus === 1,
          );
          if (alreadyExists) {
            this.focusValidationField(
              "typePensionRef",
              "El colaborador ya cuenta con el Régimen Pensionario seleccionado. Seleccione un régimen diferente o elija el existente de la lista.",
            );
            return false;
          }
        }

        if (this.isAfpPension) {
          const cuspp = (this.form.CUSPP || "").trim();
          if (!cuspp || cuspp.length !== 12) {
            this.focusValidationField(
              "cusppRef",
              "Para AFP el CUSPP es obligatorio y debe tener exactamente 12 caracteres alfanuméricos.",
            );
            return false;
          }
        }
      }

      // Validaciones Régimen de Salud
      if (!this.isHealthDisabled) {
        if (!this.form.TypeHealthRegimen) {
          this.focusValidationField(
            "typeHealthRef",
            "Seleccione el Régimen de Salud (EsSalud / EPS).",
          );
          return false;
        }

        if (this.isEpsHealth && !this.form.TypeCompany) {
          this.focusValidationField(
            "typeCompanyRef",
            "Para EPS debe seleccionar la Compañía EPS.",
          );
          return false;
        }

        // Si se seleccionó "+ Registrar nuevo régimen de salud..."
        if (
          this.selectedExistingHealthId === 0 &&
          this.financialData?.Healths?.length > 0
        ) {
          const alreadyExists = this.financialData.Healths.some(
            (h) =>
              h.TypeHealthRegimen == this.form.TypeHealthRegimen &&
              (this.form.TypeHealthRegimen != 2 ||
                h.TypeCompany == this.form.TypeCompany) &&
              h.SecStatus === 1,
          );
          if (alreadyExists) {
            this.focusValidationField(
              "typeHealthRef",
              "El colaborador ya cuenta con el Régimen de Salud seleccionado. Seleccione un régimen diferente o elija el existente de la lista.",
            );
            return false;
          }
        }
      }

      // Validaciones Cuenta Bancaria
      if (!this.isBankDisabled) {
        if (!this.form.TypeEntityBank) {
          this.focusValidationField(
            "typeEntityBankRef",
            "Seleccione la Entidad Bancaria para el pago de remuneración.",
          );
          return false;
        }

        if (!this.form.TypeMoney) {
          this.focusValidationField(
            "typeMoneyRef",
            "Seleccione la Moneda de la Cuenta Bancaria.",
          );
          return false;
        }

        if (!this.form.BatStatus) {
          this.focusValidationField(
            "batStatusRef",
            "Seleccione el Estado de la Cuenta Bancaria.",
          );
          return false;
        }

        const isFormatStrict =
          this.bankDedAbbreviation == 1 || this.bankDedAbbreviation === "1";
        const accountNum = (this.form.BatAccountNumber || "").trim();
        const cci = (this.form.BatCCI || "").trim();

        if (!accountNum) {
          this.focusValidationField(
            "batAccountNumberRef",
            "El número de cuenta bancaria es obligatorio.",
          );
          return false;
        }

        if (accountNum && isFormatStrict) {
          if (
            this.bankAccountLength &&
            accountNum.length !== this.bankAccountLength
          ) {
            this.focusValidationField(
              "batAccountNumberRef",
              `El número de cuenta debe tener exactamente ${this.bankAccountLength} dígitos para la entidad seleccionada.`,
            );
            return false;
          }

          if (
            this.accountPrefixRequired &&
            !accountNum.startsWith(this.accountPrefixRequired)
          ) {
            this.focusValidationField(
              "batAccountNumberRef",
              `El número de cuenta debe comenzar con "${this.accountPrefixRequired}" para la entidad seleccionada.`,
            );
            return false;
          }
        }

        // Si se ingresó CCI (opcional, pero si tiene valor se valida longitud y prefijo)
        if (cci) {
          const expectedCciLen = this.bankCciLength || 20;
          if (cci.length !== expectedCciLen) {
            this.focusValidationField(
              "batCciRef",
              `El CCI debe tener exactamente ${expectedCciLen} dígitos.`,
            );
            return false;
          }

          if (
            this.accountPrefixRequired &&
            !cci.startsWith(this.accountPrefixRequired)
          ) {
            this.focusValidationField(
              "batCciRef",
              `El CCI debe comenzar con "${this.accountPrefixRequired}" para la entidad seleccionada.`,
            );
            return false;
          }
        }
      }

      // Validaciones de Fechas de Inicio por Régimen
      if (!this.isPsnDateDisabled) {
        if (!this.form.AgtPsnBeginDate) {
          this.form.AgtPsnBeginDate = this.form.AgtBeginDate;
        }
      }

      if (!this.isHthDateDisabled) {
        if (!this.form.AgtHthBeginDate) {
          this.form.AgtHthBeginDate = this.form.AgtBeginDate;
        }
      }

      // Validación Estricta para Ampliación / Prórroga
      if (this.extension && this.datawork && this.datawork.AgtEndDate) {
        if (!this.form.AgtEndDate) {
          this.focusValidationField(
            "endDateRef",
            "Para realizar una ampliación debe ingresar la nueva Fecha Fin del Contrato.",
          );
          return false;
        }
        const oldEndStr = (this.datawork.AgtEndDate || "").substring(0, 10);
        if (oldEndStr && this.form.AgtEndDate <= oldEndStr) {
          this.focusValidationField(
            "endDateRef",
            `La nueva fecha fin (${this.form.AgtEndDate}) debe ser estrictamente posterior a la fecha fin anterior (${oldEndStr}).`,
          );
          return false;
        }
      }

      // Validación Estricta para Renovación
      if (this.renovation) {
        if (!this.form.AgtEndDate) {
          this.focusValidationField(
            "endDateRef",
            "Para realizar una renovación debe ingresar la Fecha Fin del Contrato.",
          );
          return false;
        }
        const beginStr = (this.form.AgtBeginDate || "").substring(0, 10);
        const endStr = (this.form.AgtEndDate || "").substring(0, 10);
        if (beginStr && endStr <= beginStr) {
          this.focusValidationField(
            "endDateRef",
            `Para una renovación, la fecha fin (${endStr}) debe ser estrictamente posterior a la fecha de inicio (${beginStr}).`,
          );
          return false;
        }
      }

      // Validación Estricta para Cese / Baja
      if (this.registerCessation) {
        if (!this.form.TypeTermination) {
          this.focusValidationField(
            "terminationRef",
            "Seleccione el Motivo de Cese para dar de baja al contrato."
          );
          return false;
        }
        if (!this.form.AgtTerminationDate) {
          this.$fun.alert(
            "Ingrese la Fecha de Cese para dar de baja al contrato.",
            "warning"
          );
          return false;
        }
        const termStr = String(this.form.AgtTerminationDate).substring(0, 10);
        const beginStr = String(this.form.AgtBeginDate || "").substring(0, 10);
        if (beginStr && termStr < beginStr) {
          this.$fun.alert(
            `La fecha de cese (${termStr}) no puede ser menor a la fecha de inicio del contrato (${beginStr}).`,
            "warning"
          );
          return false;
        }
      }

      if (this.estIsSCTRRisk && !this.form.AgtIsSCTR) {
        this.form.AgtIsSCTR = true;
        this.$emit("sctrChanged", true);
      }
      return true;
    },

    /** Retorna los datos del formulario para el guardado. null si hay error. */
    getData() {
      if (!this.validateData()) {
        return null;
      }
      if (!this.registerCessation) {
        this.form.TypeTermination = null;
        this.form.AgtTerminationDate = null;
      }
      const todayDate = new Date().toISOString().substring(0, 10);
      return {
        ...this.form,
        AgtBatBeginDate: this.isModification
          ? todayDate
          : this.form.AgtBatBeginDate || this.form.AgtBeginDate || todayDate,
        modificationScope: this.modificationScope,
        EstID:
          this.form.EstID ||
          (this.establishmentsList && this.establishmentsList.length > 0
            ? this.establishmentsList[0].EstID
            : 1),
        AgtIsSCTR: this.form.AgtIsSCTR ? 1 : 0,
        AgtMaximunDay: this.form.AgtMaximunDay ? 1 : 0,
        AgtRenovation: this.renovation ? 0 : this.form.AgtRenovation || 0,
        AgtRenewedFromID: this.form.AgtRenewedFromID || null,
        AgtStatus: this.registerCessation ? 2 : this.form.AgtStatus,
        RegisterCessation: this.registerCessation,
      };
    },
  },
};
</script>

<style scoped>
.section-card {
  border-radius: 8px !important;
}
.section-title {
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #424242;
}
.sctr-active-card {
  border-color: #ffa000 !important;
  background: #fff8e1 !important;
}
</style>
